import type { ChatProvider } from '@xsai-ext/shared-providers'

import type { MemoryItem, MemorySummary } from '../types/memory'
import type { Contact } from './contacts'
import type { Post, PostComment } from './posts'

import { useBroadcastChannel, useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { compressAvatarForPush } from '../utils/avatarCompression'
import { createWorkerTimer } from '../utils/worker-timer'
import { ApiPriority } from './api-priority-queue'
import { useCallStore } from './call'
import { useChatStore } from './chat'
import { useContactsStore } from './contacts'
import { useLLM } from './llm'
import { useAiriCardStore } from './modules/airi-card'
import { useOfflineSyncStore } from './offlineSync'
import { usePostsStore } from './posts'
import { useUserPersonaStore } from './user-persona'

/** 安全提取消息文本内容（兼容 string 和 ContentPart[] 格式） */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string')
    return content
  if (Array.isArray(content))
    return content.filter((p: { type: string }) => p.type === 'text').map((p: { text: string }) => p.text).join('')
  return ''
}

function getContactPersonaSummary(
  contactId: string | undefined,
  contactsStore: ReturnType<typeof useContactsStore>,
  airiCardStore: ReturnType<typeof useAiriCardStore>,
): string {
  if (!contactId || contactId === 'user')
    return ''

  const contact = contactsStore.getContact(contactId)
  if (!contact?.cardId)
    return ''

  const card = airiCardStore.getCard(contact.cardId)
  if (!card)
    return ''

  return [card.systemPrompt, card.description, card.personality]
    .filter(Boolean)
    .join('\n')
}

function formatUserPersonaSummary(contact: Contact | null): string {
  const userPersonaStore = useUserPersonaStore()
  const persona = userPersonaStore.resolvePersonaForContact(contact)
  if (!persona)
    return ''

  const gender = userPersonaStore.getLocalizedGender(persona)
  return [
    `名字: ${persona.name || '用户'}`,
    gender ? `性别: ${gender}` : '',
    persona.birthday ? `生日: ${persona.birthday}` : '',
    persona.persona ? `人设: ${persona.persona}` : '',
  ].filter(Boolean).join('\n')
}

function buildMomentsCommentThreadOptions(
  post: Post,
  userComment: PostComment,
  responderContact: Contact | null,
  responderId: string,
  contactsStore: ReturnType<typeof useContactsStore>,
  airiCardStore: ReturnType<typeof useAiriCardStore>,
) {
  const parentComment = userComment.replyToId ? post.comments.find(comment => comment.id === userComment.replyToId) : undefined

  return {
    responderId,
    responderPersona: getContactPersonaSummary(responderId, contactsStore, airiCardStore),
    postAuthorPersona: getContactPersonaSummary(post.authorId, contactsStore, airiCardStore),
    parentPersona: getContactPersonaSummary(parentComment?.authorId ?? userComment.replyToAuthorId, contactsStore, airiCardStore),
    userPersona: formatUserPersonaSummary(responderContact),
  }
}

function formatMomentsCommentThread(
  post: Post,
  userComment: PostComment,
  options?: {
    responderId?: string
    responderPersona?: string
    postAuthorPersona?: string
    parentPersona?: string
    userPersona?: string
  },
): string {
  const commentById = new Map(post.comments.map(comment => [comment.id, comment]))
  const parentComment = userComment.replyToId ? commentById.get(userComment.replyToId) : undefined
  const commentsSummary = post.comments.length > 0
    ? post.comments
        .map((comment) => {
          const replyTarget = comment.replyToAuthor ? ` 回复 ${comment.replyToAuthor}` : ''
          return `- ${comment.author}${replyTarget}: "${comment.text}"`
        })
        .join('\n')
    : '（暂无其他评论）'

  const parentContext = parentComment
    ? `\n\n用户这条评论是在回复 ${parentComment.author} 的评论。\n被回复的评论: ${parentComment.author}: "${parentComment.text}"`
    : userComment.replyToAuthor
      ? `\n\n用户这条评论是在回复 ${userComment.replyToAuthor}。`
      : ''

  const peopleContextParts = [
    options?.responderPersona ? `## 你（即将回复的人）的人设\n${options.responderPersona}` : '',
    post.authorId !== options?.responderId && post.authorId !== userComment.authorId && options?.postAuthorPersona ? `## 原动态作者 ${post.authorName} 的人设\n${options.postAuthorPersona}` : '',
    parentComment && options?.parentPersona ? `## 被回复者 ${parentComment.author} 的人设\n${options.parentPersona}` : '',
    options?.userPersona ? `## 用户的人设\n${options.userPersona}` : '',
    parentComment && options?.parentPersona ? '提示: 被回复者的人设只用于理解评论语气和上下文；除非评论区或你的人设明确说明你们认识，否则不要假装你认识对方。' : '',
  ].filter(Boolean)
  const peopleContext = peopleContextParts.length > 0
    ? `\n\n相关人物设定:\n${peopleContextParts.join('\n\n')}`
    : ''

  return `原动态作者: ${post.authorName}\n原动态内容: ${post.content || '（图片动态）'}${parentContext}${peopleContext}\n\n用户的评论: ${userComment.author}: "${userComment.text}"\n\n当前评论区完整上下文:\n${commentsSummary}`
}

// LLM 提供商配置
export interface ProviderConfig {
  apiKey?: string
  baseUrl?: string
  headers?: Record<string, string>
  [key: string]: unknown // 允许其他自定义字段
}

// 📡 BroadcastChannel 事件类型定义
type BroadcastChannelEvents
  = | { type: 'ai_activity_triggered', data: { timestamp: number, activityType: string } }
    | { type: 'countdown_reset', data: { timestamp: number } }
    | { type: 'block_cooldown_scan_claim', data: { timestamp: number, tabId: string } }

/**
 * 后台活动引擎
 * 让AI能够主动发起行动（发消息、打电话等）
 *
 * 优先级设计：
 * - 用户主动聊天：HIGH (10)
 * - 后台活动：LOW (1)
 * - 通过优先级队列确保用户操作始终优先
 */
export const useBackgroundActivityStore = defineStore('backgroundActivity', () => {
  const chatStore = useChatStore()
  const callStore = useCallStore()
  const contactsStore = useContactsStore()
  const airiCardStore = useAiriCardStore()
  const offlineSyncStore = useOfflineSyncStore()
  const { stream } = useLLM()

  // 📡 跨页面通信 - BroadcastChannel
  const { post: broadcastActivity, data: receivedActivity } = useBroadcastChannel<
    BroadcastChannelEvents,
    BroadcastChannelEvents
  >({ name: 'meowchat-background-activity' })

  // 是否启用后台活动（持久化到 localStorage）
  const enabled = useLocalStorage('background-activity/enabled', true)

  // ⚙️ Web Worker 后台定时器（不受主线程节流影响）
  // 主线程 setInterval 在后台会被节流到 1min+，Worker 不受此限制
  const workerTimer = createWorkerTimer()
  // 移动端长期持有 Web Lock 会阻止浏览器主动挂起页面，导致发热和耗电。
  const isMobileDevice = typeof navigator !== 'undefined'
    && (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
      || (navigator.maxTouchPoints > 0
        && typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches))

  // 💓 心跳配置（保持后台活跃）
  // ⚠️ 短周期心跳可以防止移动端浏览器暂停标签页
  const HEARTBEAT_INTERVAL = 60 // 固定 60 秒，不可配置
  let heartbeatIntervalId: number | null = null
  let engineStarted = false

  // 倒计时配置（用于主动消息）（持久化到 localStorage）
  const minHours = useLocalStorage('background-activity/min-hours', 3.0)
  const maxHours = useLocalStorage('background-activity/max-hours', 6.0)

  // 安静时间配置（不打扰时间段）（持久化到 localStorage）
  const quietTimeStart = useLocalStorage('background-activity/quiet-time-start', '23:00')
  const quietTimeEnd = useLocalStorage('background-activity/quiet-time-end', '07:00')

  // 拉黑冷静期（小时），默认 1 小时（与参考项目对齐）
  const blockCooldownHours = useLocalStorage('background-activity/block-cooldown-hours', 1)

  // 倒计时状态
  let countdownTimer: number | null = null
  let countdownEndTime: Date | null = null
  const isCountdownRunning = ref(false)

  // 🔒 Web Locks API 保活
  let webLockAbortController: AbortController | null = null

  /**
   * 🔒 启动 Web Locks API 保活
   * 持有一个永不释放的锁，浏览器会认为页面有活跃操作，降低被冻结的概率
   */
  function startWebLockKeepAlive() {
    if (isMobileDevice) {
      console.info('📱 移动端跳过 Web Lock 保活，避免阻止系统挂起页面')
      return
    }

    if (webLockAbortController) {
      return // 已在运行
    }

    if (!navigator.locks) {
      console.info('ℹ️ 当前浏览器不支持 Web Locks API，跳过')
      return
    }

    try {
      webLockAbortController = new AbortController()
      navigator.locks.request(
        'meowchat-background-keepalive',
        { mode: 'exclusive', signal: webLockAbortController.signal },
        () => new Promise<void>(() => {
          // 永不 resolve → 持续持有锁
        }),
      ).catch((err) => {
        // AbortError 是正常停止，不需要报错
        if (err.name !== 'AbortError') {
          console.warn('⚠️ Web Lock 异常:', err)
        }
      })
      console.debug('🔒 Web Locks API 保活已启动')
    }
    catch (error) {
      console.warn('⚠️ Web Locks API 保活启动失败:', error)
    }
  }

  /**
   * 🔒 停止 Web Locks API 保活
   */
  function stopWebLockKeepAlive() {
    if (webLockAbortController) {
      webLockAbortController.abort()
      webLockAbortController = null
      console.debug('🔒 Web Locks API 保活已停止')
    }
  }

  // 🔄 倒计时持久化 - 保存预计触发时间到 localStorage，刷新后可恢复
  const savedCountdownEndTime = useLocalStorage<number | null>('background-activity/countdown-end-time', null)

  // ⭐ 存储发送选项（由外部设置）- 持久化到 localStorage 以跨刷新存活
  // 注意：不持久化 providerConfig（可能含 apiKey），运行时由 ensureChatProvider 从 providersStore 获取
  const savedSendOptions = useLocalStorage<{
    providerId: string
    model: string
  } | null>('background-activity/send-options', null)

  const sendOptions = ref<{
    providerId: string
    model: string
    chatProvider: ChatProvider | null
    providerConfig?: ProviderConfig
  } | null>(null)

  // 🔄 启动时从 localStorage 恢复 sendOptions（chatProvider 不可序列化，由运行时通过 setSendOptions 注入）
  if (savedSendOptions.value) {
    try {
      const savedOptions = savedSendOptions.value
      if (!savedOptions.providerId) {
        savedSendOptions.value = null
      }
      else {
        sendOptions.value = {
          providerId: savedOptions.providerId,
          model: savedOptions.model,
          chatProvider: null,
        }
      }
    }
    catch (error) {
      console.warn('⚠️ 恢复 sendOptions 失败:', error)
      savedSendOptions.value = null
    }
  }

  /**
   * 设置发送选项（必须在启动前调用）
   * 同时持久化到 localStorage，确保刷新后仍可用
   */
  const setSendOptions = (options: { providerId: string, model: string, chatProvider: ChatProvider, providerConfig?: ProviderConfig }) => {
    sendOptions.value = options
    // 持久化到 localStorage（chatProvider 不可序列化，只存可序列化字段）
    savedSendOptions.value = {
      providerId: options.providerId,
      model: options.model,
    }
  }

  /**
   * 🔌 懒加载 chatProvider（并发安全）
   * 启动时从 localStorage 恢复的 sendOptions 中 chatProvider 为 null（不可序列化），
   * 此函数在需要 LLM 时自动从 providersStore 解析并注入 chatProvider。
   * 如果 sendOptions 完全为空（首次使用、从未聊过天），则从 consciousnessStore 读取当前激活的提供商和模型。
   * 使用 Promise 去重防止并发调用重复初始化。
   */
  let ensureProviderPromise: Promise<boolean> | null = null
  const ENSURE_PROVIDER_TIMEOUT_MS = 15 * 1000

  async function ensureChatProvider(): Promise<boolean> {
    if (sendOptions.value?.chatProvider && typeof sendOptions.value.chatProvider.chat === 'function') {
      return true
    }
    if (ensureProviderPromise)
      return ensureProviderPromise
    // 包 15s 硬超时：动态 import / providersStore.getProviderInstance / new Worker / SDK 初始化
    // 任一环节挂起会让整个 triggerMomentsSimulation 卡死，与 streamFn 卡死症状相同。
    // 超时返回 false，下次自然重试，不影响下一轮调度
    const guarded = Promise.race([
      doEnsureChatProvider(),
      new Promise<boolean>((resolve) => {
        const id = workerTimer.setTimeout(() => {
          console.warn(`⚠️ ensureChatProvider 超过 ${ENSURE_PROVIDER_TIMEOUT_MS / 1000}s 未返回，视为失败`)
          resolve(false)
          workerTimer.clearTimeout(id)
        }, ENSURE_PROVIDER_TIMEOUT_MS)
      }),
    ])
    ensureProviderPromise = guarded.finally(() => {
      ensureProviderPromise = null
    })
    return ensureProviderPromise
  }

  async function doEnsureChatProvider(): Promise<boolean> {
    let providerId = sendOptions.value?.providerId
    let model = sendOptions.value?.model

    if (!providerId || !model) {
      try {
        const { useConsciousnessStore } = await import('./modules/consciousness')
        const consciousnessStore = useConsciousnessStore()
        providerId = providerId || consciousnessStore.activeProvider
        model = model || consciousnessStore.activeModel
      }
      catch (error) {
        console.warn('⚠️ 读取 consciousnessStore 失败:', error)
      }
    }

    if (!providerId || !model) {
      return false
    }

    try {
      const { useProvidersStore } = await import('./providers')
      const providersStore = useProvidersStore()
      const chatProvider = await providersStore.getProviderInstance(providerId)
      if (chatProvider && 'chat' in (chatProvider as object) && typeof (chatProvider as unknown as Record<string, unknown>).chat === 'function') {
        const providerConfig = providersStore.getProviderConfig(providerId)
        sendOptions.value = { providerId, model, chatProvider: chatProvider as ChatProvider, providerConfig }
        return true
      }
    }
    catch (error) {
      console.warn('⚠️ 自动恢复 chatProvider 失败:', error)
    }
    return false
  }

  /**
   * 格式化时间间隔
   */
  function formatTimeDelta(milliseconds: number): string {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60))
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return `${days}天前`
    }
    if (hours > 0) {
      return `${hours}小时前`
    }
    if (minutes > 0) {
      return `${minutes}分钟前`
    }
    return '刚刚'
  }

  /**
   * 检查当前是否在安静时间段内
   */
  function isQuietTime(): boolean {
    try {
      const now = new Date()
      const currentTime = now.getHours() * 60 + now.getMinutes()

      const [startHour, startMin] = quietTimeStart.value.split(':').map(Number)
      const [endHour, endMin] = quietTimeEnd.value.split(':').map(Number)

      const quietStart = startHour * 60 + startMin
      const quietEnd = endHour * 60 + endMin

      if (quietStart <= quietEnd) {
        // 如果安静时间不跨天（例如：14:00 - 18:00）
        return currentTime >= quietStart && currentTime <= quietEnd
      }
      else {
        // 如果安静时间跨天（例如：22:00 - 08:00）
        return currentTime >= quietStart || currentTime <= quietEnd
      }
    }
    catch (error) {
      console.error('检查安静时间出错:', error)
      return false
    }
  }

  /**
   * 获取随机倒计时时间（毫秒）
   */
  function getRandomCountdownTime(): number {
    const minMs = minHours.value * 3600 * 1000
    const maxMs = maxHours.value * 3600 * 1000
    return minMs + Math.random() * (maxMs - minMs)
  }

  /**
   * 触发AI的后台行动
   * 让AI决定做什么（发消息、打电话、发动态等）
   */
  const triggerAIAction = async () => {
    // 防止在通话中触发
    if (callStore.isCallActive || callStore.outgoingCallPending) {
      console.info('通话进行中，跳过后台活动')
      return
    }

    const activeContactId = contactsStore.activeContactId
    const activeContact = activeContactId ? contactsStore.getContact(activeContactId) : null
    if (!activeContact || activeContact.isGroup) {
      console.info(activeContact?.isGroup ? '👥 当前是群聊，跳过主动消息' : '👥 当前联系人未就绪，跳过主动消息')
      return
    }
    // 拉黑过滤：被完全拉黑（blocked_by_user / blocked_by_ai）时跳过主动消息
    if (activeContactId && contactsStore.isFullyBlocked(activeContactId)) {
      console.info('🚫 当前联系人已被拉黑，跳过主动消息')
      return
    }

    console.info('🤖 ===== 后台活动引擎触发 =====')

    const timeAware = activeContact.timeAwarenessEnabled !== false

    const currentTime = timeAware
      ? new Date().toLocaleString('zh-CN', {
          dateStyle: 'full',
          timeStyle: 'short',
        })
      : ''

    // 获取最后一条消息的时间，并找出最新的待处理朋友圈评论通知。
    const lastMessage = chatStore.messages[chatStore.messages.length - 1]
    const timeSinceLastMessage = lastMessage
      ? Date.now() - (lastMessage as any).timestamp || 0
      : Infinity
    const pendingPostCommentMessage = [...chatStore.messages].reverse().find((message: any) =>
      message.metadata?.type === 'user_post_comment',
    ) as any | undefined
    const pendingPostCommentMetadata = pendingPostCommentMessage?.metadata
    const pendingPostCommentId = pendingPostCommentMetadata?.notificationId
    const removePendingPostCommentNotification = () => {
      if (!pendingPostCommentMessage)
        return

      chatStore.messages = chatStore.messages.filter((message: any) => {
        if (message.metadata?.type !== 'user_post_comment')
          return true
        if (pendingPostCommentId)
          return message.metadata?.notificationId !== pendingPostCommentId
        return message !== pendingPostCommentMessage
          && (message.timestamp !== pendingPostCommentMessage.timestamp
            || message.metadata?.postId !== pendingPostCommentMetadata?.postId
            || message.metadata?.commentText !== pendingPostCommentMetadata?.commentText)
      })
    }

    // ⭐ 检查是否有"用户评论了你的动态"的通知
    let postCommentNotification: string | null = null
    let stalePostCommentNotification = false
    if (pendingPostCommentMessage) {
      const postId = pendingPostCommentMetadata?.postId
      const commentText = pendingPostCommentMetadata?.commentText

      // 尝试获取动态详情
      try {
        const postsStore = (await import('./posts')).usePostsStore()
        const post = postsStore.posts.find(p => p.id === postId)

        if (post) {
          // 构建详细的动态评论通知
          const allComments = post.comments.map((comment) => {
            const replyTarget = comment.replyToAuthor ? ` 回复 ${comment.replyToAuthor}` : ''
            return `    - ${comment.author}${replyTarget}: "${comment.text}"`
          }).join('\n')
          const hasLiked = post.likes.includes(contactsStore.activeContact?.id || '')

          postCommentNotification = `
# 🔔 优先处理：用户评论了你的动态

你需要优先处理这条通知：

- **动态ID**: ${postId}
- **动态内容**: "${post.content}"
- **用户的评论**: "${commentText}"
- **你的点赞状态**: 你 ${hasLiked ? '已经点赞过' : '还没有点赞'}
- **所有评论**:
${allComments || '    （暂无其他评论）'}

**你的任务**：
基于你的人设和当前心情，决定是否要回复这条评论。你可以：
1. 使用工具箱中的 "comment_on_post" 回复评论
2. 如果觉得不需要回复，可以选择"保持沉默"（返回空消息数组）
`.trim()

          console.info('📬 检测到动态评论通知:', commentText)
        }
        else {
          stalePostCommentNotification = true
          console.info('🧹 评论对应的动态已不存在，清理过期通知')
        }
      }
      catch (error) {
        console.warn('获取动态详情失败:', error)
      }
    }
    if (stalePostCommentNotification)
      removePendingPostCommentNotification()

    // 📊 打印当前上下文
    if (timeAware) {
      console.info('⏰ 当前时间:', currentTime)
      console.info('📅 距上次对话:', formatTimeDelta(timeSinceLastMessage))
    }

    // 获取最近3条消息
    const recentMessages = chatStore.messages.slice(-6).filter((msg: any) => msg.role === 'user' || msg.role === 'assistant')
    console.info('💬 最近对话:')
    recentMessages.forEach((msg: any, idx) => {
      const content = extractTextContent(msg.content).substring(0, 100)
      console.info(`  ${idx + 1}. [${msg.role}]: ${content}${content.length >= 100 ? '...' : ''}`)
    })

    // 📝 构建后台活动专用的特殊提示
    // 这会被附加到增强型系统提示词之后
    const backgroundActivityPrompt = `

---

# 🔔 后台活动检测（重要）

${postCommentNotification ? `${postCommentNotification}\n\n---\n\n` : ''}

**场景说明**：
这是一个后台活动心跳检测。${timeAware ? `你已经 **${formatTimeDelta(timeSinceLastMessage)}** 没有和用户交流了。\n现在是 **${currentTime}**，你可以主动决定是否联系用户。` : '你可以主动决定是否联系用户。'}

**你的行动选择**：

1️⃣ **主动发消息**：
   - 正常回复即可
   - 可以表达想念、分享生活、询问用户在做什么等
${timeAware ? '   - 根据距上次对话的时间调整语气（时间越久，越应该表现出想念）' : ''}

2️⃣ **发起语音通话**：
   如果你决定打语音电话，直接调用 initiate_voice_call 工具。

3️⃣ **发起视频通话**：
   如果你决定打视频电话，直接调用 initiate_video_call 工具。

4️⃣ **不打扰**：
${timeAware ? '   如果现在不合适（比如深夜23:00后或清晨7:00前），说：\n   "算了，还是不打扰了"' : '   如果现在不合适，说："算了，还是不打扰了"'}

**重要提示**：
${timeAware ? '- 距离上次对话时间越久，你应该越想联系用户' : ''}
- 符合你的性格特点
${timeAware ? '- 如果是深夜或清晨，不要打电话' : ''}
- 这是你**主动发起**，不是回应用户
    `.trim()

    try {
      const providerReady = await ensureChatProvider()
      if (!providerReady) {
        console.warn('❌ chatProvider 未就绪，无法发送消息')
        return
      }

      // 打印后台活动提示
      console.info('📝 后台活动专用提示:')
      console.info(backgroundActivityPrompt)
      console.info('---')

      // ⭐ 添加后台活动提示作为隐藏的系统消息
      // 这会被附加到增强型系统提示词之后
      // 注意：增强型系统提示词（包含记忆、关系、时间感知等）会在 chat.send 中自动构建
      chatStore.messages = [...chatStore.messages, {
        role: 'system',
        content: backgroundActivityPrompt,
        isHidden: true, // 隐藏后台活动系统消息
      } as any]

      console.debug('⏳ 等待AI决策（将使用增强型系统提示词 + 后台活动提示）...')

      // ⭐ 实际触发AI回复（使用低优先级）
      // chat.send 会自动构建增强型系统提示词，包含：
      // - PART 0: 相关记忆（短期历史 + 长期摘要）
      // - PART 1: 核心身份（角色卡、人设）
      // - PART 2: 时空设定（当前时间、时间间隔）
      // - PART 3: 关系网络（好感度）
      // - PART 4: 输出格式
      // 然后会附加我们刚添加的后台活动提示
      await chatStore.send('', {
        providerId: sendOptions.value!.providerId,
        model: sendOptions.value!.model,
        chatProvider: sendOptions.value!.chatProvider!,
        providerConfig: sendOptions.value!.providerConfig,
        skipUserMessage: true,
        priority: ApiPriority.LOW, // 🔐 后台活动使用低优先级
        requester: 'background_activity', // 🔐 标识为后台活动任务
        ...(postCommentNotification ? { taskKind: 'post-comment' as const } : {}),
      })

      if (postCommentNotification)
        removePendingPostCommentNotification()

      console.debug('✅ AI决策请求已发送（使用完整的增强型上下文）')

      // 📡 同步到 push-server，告知 AI 刚发过消息（避免离线调度重复发送）
      chatStore.syncToOfflineServer({ userReplied: false }).catch(() => {})

      // 📡 广播后台活动触发事件到其他页面
      broadcastActivity({
        type: 'ai_activity_triggered',
        data: {
          timestamp: Date.now(),
          activityType: 'proactive_message',
        },
      })
    }
    catch (error) {
      console.error('❌ 后台活动触发失败:', error)
    }
  }

  /**
   * 检查用户是否正在活跃聊天
   * 如果用户正在发送消息，或者最近 5 分钟内有用户消息，则认为用户活跃
   */
  function isUserActivelyChating(): boolean {
    // 1. 检查 AI 是否正在回复
    if (chatStore.sending) {
      console.debug('🔍 用户活跃检测: AI 正在回复中')
      return true
    }

    // 2. 检查最近 5 分钟内是否有用户消息
    const ACTIVE_THRESHOLD = 5 * 60 * 1000 // 5 分钟
    const now = Date.now()
    const recentMessages = chatStore.messages.slice(-10) // 检查最近 10 条消息

    for (let i = recentMessages.length - 1; i >= 0; i--) {
      const msg = recentMessages[i] as any
      if (msg.role === 'user' && msg.timestamp) {
        const timeSince = now - msg.timestamp
        if (timeSince < ACTIVE_THRESHOLD) {
          console.debug(`🔍 用户活跃检测: ${Math.round(timeSince / 1000)}秒前有用户消息，判定为活跃`)
          return true
        }
        break // 只检查最近一条用户消息
      }
    }

    return false
  }

  /**
   * 自动发送消息（倒计时结束时执行）
   * 注意：必须在 startCountdown 之前声明
   */
  async function autoSendMessage() {
    if (!enabled.value) {
      return
    }

    console.info('⏰ 倒计时结束，准备主动发送消息...')

    // 🔒 检查用户是否正在活跃聊天，避免主动消息突然插入对话
    if (isUserActivelyChating()) {
      console.info('💬 用户正在活跃聊天，跳过本次主动消息，重新开始倒计时')
      startCountdown()
      return
    }

    // 检查是否在安静时间
    if (isQuietTime()) {
      console.info('🌙 当前处于安静时间，跳过本次触发，重新开始倒计时')
      startCountdown()
      return
    }

    // 直接触发AI活动（无需概率判断）
    try {
      await triggerAIAction()
    }
    catch (error) {
      console.error('❌ 自动发送消息失败:', error)
    }
    finally {
      // 无论成功失败，都重新开始倒计时
      startCountdown()
    }
  }

  /**
   * 开始新的倒计时
   */
  function startCountdown() {
    // 取消现有的倒计时（使用 Worker 定时器）
    if (countdownTimer) {
      workerTimer.clearTimeout(countdownTimer)
      countdownTimer = null
    }

    if (!enabled.value) {
      console.info('⏸️  后台活动已禁用，不启动倒计时')
      isCountdownRunning.value = false
      savedCountdownEndTime.value = null
      return
    }

    const countdownMs = getRandomCountdownTime()
    countdownEndTime = new Date(Date.now() + countdownMs)

    // 🔄 持久化预计触发时间
    savedCountdownEndTime.value = countdownEndTime.getTime()

    const hours = (countdownMs / (1000 * 60 * 60)).toFixed(2)
    console.info(`⏳ 开始新的倒计时: ${hours}小时`)
    console.info(`📅 预计触发时间: ${countdownEndTime.toLocaleString('zh-CN')}`)

    // ⚙️ 使用 Worker 定时器：即使主线程被冻结，Worker 仍可能触发
    countdownTimer = workerTimer.setTimeout(autoSendMessage, countdownMs)
    isCountdownRunning.value = true
  }

  /**
   * 🔄 从 localStorage 恢复倒计时（页面刷新后调用）
   * - 如果保存的触发时间已过期 → 立即触发
   * - 如果保存的触发时间未到 → 设置剩余时间的 setTimeout
   */
  function recoverCountdown() {
    if (!enabled.value || !savedCountdownEndTime.value) {
      return false
    }

    const savedEnd = savedCountdownEndTime.value
    const now = Date.now()
    const remaining = savedEnd - now

    if (remaining <= 0) {
      // 倒计时已过期（页面关闭期间应该触发了）
      // ⚠️ 不补触发！用户打开页面 = 用户在，补发主动消息没意义
      console.info('🔄 检测到过期的倒计时，丢弃并重新开始新倒计时')
      savedCountdownEndTime.value = null
      startCountdown()
      return true
    }
    else {
      // 倒计时尚未到期，恢复剩余时间
      const remainingHours = (remaining / (1000 * 60 * 60)).toFixed(2)
      console.debug(`🔄 恢复倒计时，剩余: ${remainingHours}小时`)

      countdownEndTime = new Date(savedEnd)
      countdownTimer = workerTimer.setTimeout(autoSendMessage, remaining)
      isCountdownRunning.value = true
      return true
    }
  }

  /**
   * 重置倒计时（用户回复时调用）
   */
  const resetCountdown = () => {
    console.info('🔄 用户回复，重置倒计时')
    startCountdown()

    // 📡 广播倒计时重置事件到其他页面
    broadcastActivity({
      type: 'countdown_reset',
      data: { timestamp: Date.now() },
    })
  }

  // 朋友圈生成相关状态
  const lastMomentsCheck = useLocalStorage('background-activity/last-moments-check', 0)
  // ⭐ 上次"真正产出动态"的时间戳；区别于 lastMomentsCheck（每次尝试都刷新）
  // 兜底逻辑（12h 强制选一个、超期立即触发、离线 2h 触发）都基于这个时间戳，
  // 否则空跑也会刷新 lastMomentsCheck，让兜底永远走不到
  const lastMomentsProduced = useLocalStorage('background-activity/last-moments-produced', 0)
  const MOMENTS_MIN_OFFLINE_HOURS = 2 // 用户离线最少 2 小时才触发朋友圈生成
  const MOMENTS_RECHECK_MIN_MS = 30 * 60 * 1000 // 离线检查防刷屏：30 分钟内尝试过就不再尝试

  // 🎲 每个联系人的朋友圈发布权重记录（contactId -> 上次发布时间戳）
  const contactPostWeights = useLocalStorage<Record<string, number>>('background-activity/contact-post-weights', {})

  // 每次模拟最多允许几个联系人发动态
  const MAX_POSTS_PER_SIMULATION = 3

  // 定期朋友圈模拟配置
  const MOMENTS_CHECK_BASE_MS = 90 * 60 * 1000 // 基础间隔 1.5h
  const MOMENTS_CHECK_JITTER_MS = 30 * 60 * 1000 // ±30min 抖动
  let momentsPeriodicTimer: number | null = null
  let isSchedulingMoments = false

  // 🔄 定期模拟下次预计触发时间（持久化，参考 savedCountdownEndTime 设计）
  // 手机后台杀进程或页面刷新后，整个调度链会丢失；通过此字段在 start() 时恢复
  const savedMomentsCheckEndTime = useLocalStorage<number | null>('background-activity/moments-check-end-time', null)

  // 追踪所有延迟响应 timer，确保 stop() 能清理
  const responseTimerIds = new Map<string, number>()

  // 持久化待触发的朋友圈响应（跨会话存活）
  interface PendingMomentsResponse {
    id: string // `${postId}-${contactId}`
    postId: string
    contactId: string
    contactName: string
    triggerTime: number
    type: 'mentioned' | 'visible' | 'comment_reply'
    affection: number
    userCommentText?: string
    userCommentId?: string
    isMentioned?: boolean
  }
  const pendingMomentsResponses = useLocalStorage<PendingMomentsResponse[]>('background-activity/pending-moments-responses', [])

  function clearPendingResponseTimer(id: string): void {
    const timerId = responseTimerIds.get(id)
    if (timerId === undefined)
      return
    workerTimer.clearTimeout(timerId)
    responseTimerIds.delete(id)
  }

  function schedulePendingResponse(item: PendingMomentsResponse, delay: number): void {
    clearPendingResponseTimer(item.id)
    const timerId = workerTimer.setTimeout(() => {
      responseTimerIds.delete(item.id)
      executePendingResponse(item).catch(err =>
        console.error(`  ❌ 延迟恢复 ${item.contactName} 响应失败:`, err),
      )
    }, delay)
    responseTimerIds.set(item.id, timerId)
  }

  function savePendingResponse(item: PendingMomentsResponse) {
    clearPendingResponseTimer(item.id)
    const list = pendingMomentsResponses.value.filter(p => p.id !== item.id)
    list.push(item)
    pendingMomentsResponses.value = list
  }

  function removePendingResponse(id: string): boolean {
    clearPendingResponseTimer(id)
    const before = pendingMomentsResponses.value.length
    pendingMomentsResponses.value = pendingMomentsResponses.value.filter(p => p.id !== id)
    return pendingMomentsResponses.value.length < before
  }

  /**
   * 恢复持久化的待触发响应（app 启动 / 从后台恢复时调用）
   * 已过期的立即执行，未到期的重新调度 timer
   */
  let isRecoveringPending = false
  async function recoverPendingResponses() {
    if (isRecoveringPending)
      return
    isRecoveringPending = true
    try {
      const pending = [...pendingMomentsResponses.value]
      if (pending.length === 0)
        return

      console.info(`🔄 恢复 ${pending.length} 个待触发的朋友圈响应`)
      const now = Date.now()
      const PENDING_EXPIRY_MS = 24 * 60 * 60 * 1000

      for (const item of pending) {
        const remaining = item.triggerTime - now

        if (-remaining > PENDING_EXPIRY_MS) {
          console.info(`  🗑️ ${item.contactName} 的响应已过期超过24h，丢弃`)
          removePendingResponse(item.id)
          continue
        }

        if (remaining <= 0) {
          console.info(`  ⏰ ${item.contactName} 的响应已过期 (${Math.round(-remaining / 60000)}min前)，立即执行`)
          executePendingResponse(item).catch(err =>
            console.error(`  ❌ 恢复 ${item.contactName} 响应失败:`, err),
          )
        }
        else {
          console.info(`  ⏳ ${item.contactName} 还需等待 ${(remaining / 60000).toFixed(1)}min`)
          schedulePendingResponse(item, remaining)
        }
      }
    }
    finally {
      isRecoveringPending = false
    }
  }

  /**
   * 执行单个持久化的待触发响应
   */
  async function executePendingResponse(item: PendingMomentsResponse) {
    // 先移除，防止重复执行
    if (!removePendingResponse(item.id)) {
      return // 已被其他路径处理
    }

    try {
      const providerReady = await ensureChatProvider()
      if (!providerReady) {
        console.warn(`  ❌ chatProvider 未就绪，跳过 ${item.contactName} 的响应`)
        return
      }

      const { usePostsStore } = await import('./posts')
      const postsStore = usePostsStore()
      const post = postsStore.posts.find(p => p.id === item.postId)
      if (!post) {
        console.warn(`  ❌ 找不到动态 ${item.postId}，跳过`)
        return
      }

      if (item.type === 'comment_reply') {
        // 用户评论后的 AI 回复恢复
        const isMentioned = item.isMentioned ?? false
        const isOwnPost = post.authorId === item.contactId
        let contextDescription = ''
        if (isMentioned) {
          contextDescription = '用户在你的朋友圈动态下评论并@了你'
        }
        else if (isOwnPost) {
          contextDescription = '用户在你发布的朋友圈动态下评论了'
        }
        else {
          contextDescription = '用户在朋友圈动态下发表了评论'
        }
        const userComment = [...post.comments].reverse().find(comment =>
          item.userCommentId
            ? comment.id === item.userCommentId
            : comment.authorId === 'user' && comment.text === item.userCommentText,
        )
        const responderContact = contactsStore.getContact(item.contactId)
        const fullContext = `${contextDescription}。\n\n${userComment
          ? formatMomentsCommentThread(
              post,
              userComment,
              buildMomentsCommentThreadOptions(post, userComment, responderContact, item.contactId, contactsStore, airiCardStore),
            )
          : `原动态内容: ${post.content || '（图片动态）'}\n\n用户的评论: ${item.userCommentText || ''}`}`
        const replyText = await generateAIComment(
          item.contactId,
          item.contactName,
          fullContext,
          isMentioned,
          post.imageUrl,
          post.imageDescription,
          'comment_reply',
        )
        if (replyText) {
          postsStore.aiCommentOnPost(
            item.postId,
            replyText,
            item.contactId,
            item.contactName,
            userComment
              ? { id: userComment.id, author: userComment.author, authorId: userComment.authorId }
              : undefined,
          )
          console.info(`  💬 ${item.contactName} 恢复回复: ${replyText.substring(0, 30)}...`)
        }
      }
      else if (item.type === 'mentioned') {
        // 被 @ 联系人：点赞 + 必须评论
        const shouldLike = Math.random() < getMentionedLikeProbability(item.affection)
        if (shouldLike) {
          postsStore.aiLikePost(item.postId, item.contactId, item.contactName)
          console.info(`  ❤️  ${item.contactName} 点赞了动态`)
        }
        const commentText = await generateAIComment(
          item.contactId,
          item.contactName,
          post.content || '（图片动态）',
          true,
          post.imageUrl,
          post.imageDescription,
        )
        if (commentText) {
          postsStore.aiCommentOnPost(item.postId, commentText, item.contactId, item.contactName)
          console.info(`  💬 ${item.contactName} 评论: ${commentText.substring(0, 30)}...`)
        }
      }
      else {
        // 可见联系人：根据关系决定响应方式
        const responseConfig = getRelationshipResponseConfig(item.affection)
        const rand = Math.random()
        const { likeOnlyRatio, likeAndCommentRatio } = responseConfig

        if (rand < likeOnlyRatio) {
          postsStore.aiLikePost(item.postId, item.contactId, item.contactName)
          console.info(`  ❤️  ${item.contactName} 点赞了动态`)
        }
        else if (rand < likeOnlyRatio + likeAndCommentRatio) {
          postsStore.aiLikePost(item.postId, item.contactId, item.contactName)
          const commentText = await generateAIComment(
            item.contactId,
            item.contactName,
            post.content || '（图片动态）',
            false,
            post.imageUrl,
            post.imageDescription,
          )
          if (commentText) {
            postsStore.aiCommentOnPost(item.postId, commentText, item.contactId, item.contactName)
            console.info(`  💬 ${item.contactName} 评论: ${commentText.substring(0, 30)}...`)
          }
        }
        else {
          const commentText = await generateAIComment(
            item.contactId,
            item.contactName,
            post.content || '（图片动态）',
            false,
            post.imageUrl,
            post.imageDescription,
          )
          if (commentText) {
            postsStore.aiCommentOnPost(item.postId, commentText, item.contactId, item.contactName)
            console.info(`  💬 ${item.contactName} 评论: ${commentText.substring(0, 30)}...`)
          }
        }
      }
    }
    catch (error) {
      console.error(`  ❌ ${item.contactName} 恢复响应失败:`, error)
    }
  }

  /**
   * 🕐 生成自然的回溯时间戳（时段感知）
   * 避开深夜（0:00-6:00），在活跃时段（8:00-23:00）聚集
   * 使用拒绝采样：随机生成候选时间，如果落在低活跃时段则大概率重新生成
   */
  function generateNaturalTimestamp(rangeStart: number, rangeEnd: number): number {
    const MAX_ATTEMPTS = 20

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      // 在范围内均匀随机一个候选时间
      const candidate = rangeStart + Math.random() * (rangeEnd - rangeStart)
      const hour = new Date(candidate).getHours()

      // 根据小时计算"接受概率"——模拟真实社交媒体活跃度曲线
      // 0-5点: 极低(5%)  6-7点: 低(20%)  8-9点: 中(60%)
      // 10-12点: 高(90%) 13-14点: 中高(70%) 15-17点: 高(85%)
      // 18-21点: 峰值(95%) 22-23点: 中(50%)
      const hourWeights: Record<number, number> = {
        0: 0.05,
        1: 0.03,
        2: 0.02,
        3: 0.02,
        4: 0.03,
        5: 0.05,
        6: 0.15,
        7: 0.25,
        8: 0.55,
        9: 0.70,
        10: 0.85,
        11: 0.90,
        12: 0.80,
        13: 0.70,
        14: 0.75,
        15: 0.80,
        16: 0.85,
        17: 0.85,
        18: 0.90,
        19: 0.95,
        20: 0.95,
        21: 0.90,
        22: 0.55,
        23: 0.30,
      }

      const acceptProbability = hourWeights[hour] ?? 0.5

      if (Math.random() < acceptProbability) {
        // ±5分钟随机抖动，使时间不那么整
        const jitter = (Math.random() - 0.5) * 10 * 60 * 1000
        return Math.max(rangeStart, Math.min(candidate + jitter, rangeEnd - 60000))
      }
    }

    // 兜底：如果多次尝试都被拒绝（比如离线时段全在深夜），直接返回范围末尾附近
    return rangeEnd - Math.random() * 30 * 60 * 1000 // 最近30分钟内
  }

  /**
   * 💕 获取联系人的好感度（affection）
   * 用于关系感知的响应概率和延迟计算
   * @returns affection 值 (-100 ~ +100)，获取失败返回 0
   */
  async function getContactAffection(contactId: string): Promise<number> {
    try {
      const { useRelationshipStore } = await import('./relationship')
      const relationshipStore = useRelationshipStore()
      const relationship = relationshipStore.getRelationship(contactId)
      return relationship?.affection ?? 0
    }
    catch {
      return 0
    }
  }

  /**
   * ⏱️ 根据关系亲密度计算响应延迟（毫秒）
   * 亲密的人回复更快，陌生人回复更慢
   * - 好感度 80+（恋人/挚友）: 1-5 分钟
   * - 好感度 40-79（好友）: 3-15 分钟
   * - 好感度 0-39（普通）: 5-30 分钟
   * - 好感度 <0（冷淡/敌对）: 10-60 分钟
   */
  function calculateResponseDelay(affection: number): number {
    let minMinutes: number
    let maxMinutes: number

    if (affection >= 80) {
      minMinutes = 1
      maxMinutes = 5
    }
    else if (affection >= 40) {
      minMinutes = 3
      maxMinutes = 15
    }
    else if (affection >= 0) {
      minMinutes = 5
      maxMinutes = 30
    }
    else {
      minMinutes = 10
      maxMinutes = 60
    }

    const delayMinutes = minMinutes + Math.random() * (maxMinutes - minMinutes)
    return delayMinutes * 60 * 1000
  }

  /**
   * 💕 根据关系亲密度计算响应概率和方式
   * 亲密的人更可能互动，且互动方式更丰富
   * @returns { shouldRespond, likeProb, commentProb } 响应概率配置
   */
  function getRelationshipResponseConfig(affection: number): {
    respondProbability: number // 是否响应的概率
    likeOnlyRatio: number // 只点赞的比例
    likeAndCommentRatio: number // 点赞+评论的比例
    commentOnlyRatio: number // 只评论的比例
  } {
    if (affection >= 80) {
      // 恋人/挚友：高互动
      return { respondProbability: 0.70, likeOnlyRatio: 0.20, likeAndCommentRatio: 0.60, commentOnlyRatio: 0.20 }
    }
    else if (affection >= 50) {
      // 好友：中高互动
      return { respondProbability: 0.50, likeOnlyRatio: 0.35, likeAndCommentRatio: 0.45, commentOnlyRatio: 0.20 }
    }
    else if (affection >= 20) {
      // 熟人：中等互动
      return { respondProbability: 0.30, likeOnlyRatio: 0.55, likeAndCommentRatio: 0.30, commentOnlyRatio: 0.15 }
    }
    else if (affection >= 0) {
      // 普通：低互动
      return { respondProbability: 0.15, likeOnlyRatio: 0.70, likeAndCommentRatio: 0.20, commentOnlyRatio: 0.10 }
    }
    else {
      // 冷淡/敌对：极低互动
      return { respondProbability: 0.05, likeOnlyRatio: 0.80, likeAndCommentRatio: 0.10, commentOnlyRatio: 0.10 }
    }
  }

  /**
   * 💕 被 @ 联系人的点赞概率（关系感知）
   * 亲密的人几乎必赞，冷淡的人勉强回复不太想赞
   */
  function getMentionedLikeProbability(affection: number): number {
    if (affection >= 80)
      return 0.95
    if (affection >= 50)
      return 0.85
    if (affection >= 20)
      return 0.70
    if (affection >= 0)
      return 0.50
    return 0.30
  }

  /**
   * 解析 HH:MM 格式时间字符串，无效时返回回退值
   */
  function parseTimeString(timeStr: string, fallbackHour: number, fallbackMin: number): [number, number] {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/)
    if (!match)
      return [fallbackHour, fallbackMin]
    const hour = Number.parseInt(match[1], 10)
    const min = Number.parseInt(match[2], 10)
    if (hour < 0 || hour > 23 || min < 0 || min > 59)
      return [fallbackHour, fallbackMin]
    return [hour, min]
  }

  /**
   * 🌙 安静时间内的额外延迟（好感度感知）
   * 亲密的人秒回不受安静时间影响，不熟的人等到早上
   */
  function getQuietTimeDelay(affection: number): number {
    const now = new Date()
    const [endHour, endMin] = parseTimeString(quietTimeEnd.value, 7, 0)
    const wakeUp = new Date(now)
    wakeUp.setHours(endHour, endMin, 0, 0)
    if (wakeUp <= now)
      wakeUp.setDate(wakeUp.getDate() + 1)
    const msUntilWake = wakeUp.getTime() - now.getTime()

    if (affection >= 80)
      return 0
    if (affection >= 50)
      return Math.random() * 15 * 60 * 1000
    if (affection >= 20)
      return msUntilWake + Math.random() * 15 * 60 * 1000
    return msUntilWake + Math.random() * 60 * 60 * 1000
  }

  /**
   * 🎲 计算联系人的发布概率（基于权重衰减 + 角色个性）
   * - 最近发过动态的联系人概率降低，避免同一个人刷屏
   * - 使用平滑的指数衰减曲线代替阶梯函数
   * - 根据角色人设推断社交活跃度，调整基础概率
   */
  function getContactPostProbability(contactId: string): number {
    // 根据角色个性调整基础概率（社交达人 vs 沉默寡言）
    const baseProbability = getPersonalityBasedProbability(contactId)
    const lastPostTime = contactPostWeights.value[contactId] || 0

    if (lastPostTime === 0) {
      return baseProbability // 从未发过，满概率
    }

    const hoursSinceLastPost = (Date.now() - lastPostTime) / (1000 * 60 * 60)

    // 使用平滑的指数衰减曲线：probability = base * (1 - e^(-hours/τ))
    // τ (tau) = 8 小时，即 8 小时后恢复到 ~63% 的基础概率
    const tau = 8
    const recoveryFactor = 1 - Math.exp(-hoursSinceLastPost / tau)
    return baseProbability * recoveryFactor
  }

  /**
   * 🎭 根据角色个性推断社交活跃度
   * 分析角色卡人设中的关键词，判断该角色在社交媒体上的活跃程度
   */
  function getPersonalityBasedProbability(contactId: string): number {
    const DEFAULT_PROBABILITY = 0.4

    try {
      const contact = contactsStore.getContact(contactId)
      if (!contact?.cardId)
        return DEFAULT_PROBABILITY

      const card = airiCardStore.getCard(contact.cardId)
      if (!card)
        return DEFAULT_PROBABILITY

      const persona = [card.systemPrompt, card.description, card.personality]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      // 社交活跃关键词 → 提高概率
      const activeKeywords = ['外向', '活泼', '话多', '社交', '分享', '热情', '开朗', '爱玩', '网红', '博主', '自拍']
      // 社交低频关键词 → 降低概率
      const quietKeywords = ['内向', '安静', '沉默', '冷淡', '孤僻', '不善言辞', '话少', '高冷', '独来独往']

      let score = 0
      for (const kw of activeKeywords) {
        if (persona.includes(kw))
          score++
      }
      for (const kw of quietKeywords) {
        if (persona.includes(kw))
          score--
      }

      // score 范围大约 -3 ~ +3，映射到 0.15 ~ 0.65
      return Math.max(0.15, Math.min(0.65, DEFAULT_PROBABILITY + score * 0.08))
    }
    catch {
      return DEFAULT_PROBABILITY
    }
  }

  /**
   * 为单个联系人执行朋友圈模拟
   */
  async function simulateMomentsForContact(
    contact: { id: string, name: string, avatar?: string, cardId?: string, boundPersonaId?: number | null, worldBookIds?: string[] },
    allContactNames: string[],
    allContactIds: string[],
    postsStore: ReturnType<typeof usePostsStore>,
    userNickname: string,
    streamFn: typeof stream,
    backdatedTimestamp?: number, // 回溯时间戳，让动态看起来是在离线期间发布的
  ) {
    console.info(`🎭 为 ${contact.name} 执行朋友圈模拟...`)

    const { buildMomentsSimulationPrompt } = await import('../composables/useMomentsPrompt')
    const { buildMomentsSimulationTools } = await import('../tools/chat-actions')

    // 根据联系人绑定的人格解析用户昵称（绑定人格名 → 默认人格名 → 传入的 fallback）
    const resolvedPersona = useUserPersonaStore().resolvePersonaForContact(contact)
    const resolvedUserNickname = resolvedPersona?.name || userNickname

    // 获取对该 AI 可见的最近动态
    const visiblePosts = postsStore.getVisiblePosts(contact.id).slice(0, 10)

    // 获取角色卡人设
    let characterPersona = '一个友好的AI助手'
    if (contact.cardId) {
      const card = airiCardStore.getCard(contact.cardId)
      if (card) {
        const components = [card.systemPrompt, card.description, card.personality].filter(Boolean)
        characterPersona = components.join('\n') || characterPersona
      }
    }

    // ===== 🧠 收集增强上下文 =====
    const timeAware = contactsStore.getContact(contact.id)?.timeAwarenessEnabled !== false
    let memoryContext = ''
    try {
      const { useMemoryStore } = await import('./memory')
      const memoryStore = useMemoryStore()
      const previousContactId = memoryStore.currentContactId
      memoryStore.setCurrentContact(contact.id)

      const searchResult = memoryStore.retrieveRelevantMemories('朋友圈 动态 日常')

      const recentSummaries = searchResult.summaries.slice(0, 3)
      if (recentSummaries.length > 0) {
        const summaryTexts = recentSummaries.map((s: MemorySummary) => {
          return timeAware
            ? `- (${memoryStore.formatRelativeTime(s.endTime)}) ${s.content}`
            : `- ${s.content}`
        }).join('\n')
        memoryContext += `\n## 与用户的对话记忆\n${summaryTexts}\n`
      }

      const relevantMemories = searchResult.memories.slice(0, 5)
      if (relevantMemories.length > 0) {
        const memoryTexts = relevantMemories.map((m: MemoryItem) => {
          const tag = m.isImportant ? '[核心]' : ''
          return timeAware
            ? `- ${tag}(${memoryStore.formatRelativeTime(m.timestamp)}) ${m.content}`
            : `- ${tag}${m.content}`
        }).join('\n')
        memoryContext += `\n## 关键记忆\n${memoryTexts}\n`
      }

      if (previousContactId) {
        memoryStore.setCurrentContact(previousContactId)
      }
    }
    catch (error) {
      console.warn(`朋友圈模拟(${contact.name})：获取记忆上下文失败:`, error)
    }

    let relationshipContext = ''
    try {
      const { useRelationshipStore } = await import('./relationship')
      const relationshipStore = useRelationshipStore()
      const relationship = relationshipStore.getRelationship(contact.id)
      if (relationship) {
        const description = relationshipStore.getRelationshipDescription(contact.id)
        relationshipContext = `\n## 与用户的关系\n${description}\n`
      }
    }
    catch (error) {
      console.warn(`朋友圈模拟(${contact.name})：获取关系上下文失败:`, error)
    }

    let chatContext = ''
    try {
      const recentMessages = chatStore.getMessagesForContact(contact.id)
      if (recentMessages && recentMessages.length > 0) {
        const lastMessages = recentMessages
          .filter((m: unknown) => (m as { role: string }).role === 'user' || (m as { role: string }).role === 'assistant')
          .slice(-20)
        if (lastMessages.length > 0) {
          const chatTexts = lastMessages.map((m: unknown) => {
            const msg = m as { role: string, content?: string }
            const role = msg.role === 'user' ? '用户' : contact.name
            const text = extractTextContent(msg.content)
            const content = text.substring(0, 80)
            return `- ${role}: ${content}${text.length > 80 ? '...' : ''}`
          }).join('\n')
          chatContext = `\n## 最近的私聊记录\n${chatTexts}\n`
        }
      }
    }
    catch (error) {
      console.warn(`朋友圈模拟(${contact.name})：获取聊天记录失败:`, error)
    }

    let callContext = ''
    try {
      const recentCalls = callStore.getCallLogsByCharacter(contact.id)
      if (recentCalls && recentCalls.length > 0) {
        const lastCalls = recentCalls.slice(0, 3)
        const callTexts = lastCalls.map((log: unknown) => {
          const l = log as { startTime: number, duration?: number, type?: string }
          const duration = l.duration ? `${Math.round(l.duration / 60)}分钟` : '未接通'
          const typeText = l.type === 'video' ? '视频' : '语音'
          return timeAware
            ? `- ${new Date(l.startTime).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} ${typeText}通话 (${duration})`
            : `- ${typeText}通话 (${duration})`
        }).join('\n')
        callContext = `\n## 最近的通话记录\n${callTexts}\n`
      }
    }
    catch (error) {
      console.warn(`朋友圈模拟(${contact.name})：获取通话记录失败:`, error)
    }

    let worldBookContext = ''
    try {
      if (contact.worldBookIds && contact.worldBookIds.length > 0) {
        const { useWorldBookStore } = await import('./worldbook')
        const worldBookStore = useWorldBookStore()
        const allowedIdSet = new Set(contact.worldBookIds)
        // 用最近聊天记录作为关键词匹配的输入文本
        const chatText = chatContext.replace(/^[^#]*##[^\n]*\n/m, '').replace(/^- [^:]*: /gm, '')
        const relevantBooks = worldBookStore.getRelevantWorldBooks(chatText)
          .filter(book => allowedIdSet.has(book.id))
        if (relevantBooks.length > 0) {
          worldBookContext = `\n## 世界设定\n${worldBookStore.formatWorldBooksForPrompt(relevantBooks)}\n`
        }
      }
    }
    catch (error) {
      console.warn(`朋友圈模拟(${contact.name})：获取世界书上下文失败:`, error)
    }

    // 构建 Prompt
    const promptContext = {
      characterName: contact.name,
      characterPersona,
      characterId: contact.id,
      recentPosts: visiblePosts,
      allContactNames,
      allContactIds,
      userNickname: resolvedUserNickname,
      lastActivityTime: timeAware ? (contactPostWeights.value[contact.id] || 0) : undefined,
      memoryContext,
      relationshipContext,
      chatContext,
      callContext,
      worldBookContext,
      timeAware: contactsStore.getContact(contact.id)?.timeAwarenessEnabled !== false,
    }

    const systemPrompt = buildMomentsSimulationPrompt(promptContext)

    let fullResponse = ''
    // outcome 状态机：'failed' 表示技术故障（不污染 weights）；'silent' 表示 AI 主动选择不发（轻量惩罚）；
    // 'posted' 表示真发了帖（正常冷却）。默认 failed，仅在解析成功后才能升级到 silent，再进一步升级到 posted。
    let outcome: 'posted' | 'silent' | 'failed' = 'failed'

    // 调用 LLM —— 必须有硬超时，否则坏 stream（既不 emit finish 也不 emit error）
    // 会让 isMomentsSimulating 永久卡死，导致定期 timer 醒来都被挡住，朋友圈彻底没动态
    //
    // 超时策略（思考模型友好）：
    // - HARD: 总时长上限 180s（claude-opus-4 / o1 / DeepSeek-R1 含思考阶段约 30-90s 首字 + 生成可能 60-120s）
    // - IDLE: 90s 无新增字节视为卡死（区分"慢但正常"和"完全没响应"）
    const LLM_HARD_TIMEOUT_MS = 180 * 1000
    const LLM_IDLE_TIMEOUT_MS = 90 * 1000
    try {
      await new Promise<void>((resolve, reject) => {
        let settled = false
        let idleTimer: number | null = null
        let hardTimerId: number = 0
        const safeResolve = () => {
          if (settled)
            return
          settled = true
          workerTimer.clearTimeout(hardTimerId)
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          resolve()
        }
        const safeReject = (err: unknown) => {
          if (settled)
            return
          settled = true
          workerTimer.clearTimeout(hardTimerId)
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          reject(err)
        }
        const resetIdleTimer = () => {
          if (settled)
            return
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          idleTimer = workerTimer.setTimeout(() => {
            safeReject(new Error(`LLM stream 空闲 ${LLM_IDLE_TIMEOUT_MS / 1000}s 无新增字节`))
          }, LLM_IDLE_TIMEOUT_MS)
        }
        hardTimerId = workerTimer.setTimeout(() => {
          safeReject(new Error(`LLM stream 总时长超过 ${LLM_HARD_TIMEOUT_MS / 1000}s 仍未完成`))
        }, LLM_HARD_TIMEOUT_MS)
        resetIdleTimer()

        // 构建朋友圈专用工具集
        const momentsTools = buildMomentsSimulationTools({
          contactId: contact.id,
          contactName: contact.name,
          contactAvatar: contact.avatar,
          postsStore: {
            createPost: (params) => {
              postsStore.createPost({
                ...params,
                ...(backdatedTimestamp && { timestamp: backdatedTimestamp }),
              })
            },
            aiLikePost: postsStore.aiLikePost.bind(postsStore),
            aiCommentOnPost: postsStore.aiCommentOnPost.bind(postsStore),
          },
          allContactNames,
          allContactIds,
          userNickname: resolvedUserNickname,
          backdatedTimestamp,
          onActionExecuted: (actionType) => {
            // 工具被调用时更新 outcome
            if (actionType === 'create_post') {
              outcome = 'posted'
            }
            else if (actionType !== 'do_nothing' && outcome === 'failed') {
              // like/comment 至少升级为 silent
              outcome = 'silent'
            }
          },
        })

        streamFn(
          sendOptions.value!.model,
          sendOptions.value!.chatProvider!,
          [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: '请基于当前情况，决定你的朋友圈行动。' },
          ],
          {
            headers: sendOptions.value!.providerConfig?.headers || {},
            tools: momentsTools,
            supportsTools: true,
            async onStreamEvent(event) {
              try {
                if (event.type === 'text-delta' && event.text) {
                  fullResponse += event.text
                  resetIdleTimer()
                }
                else if (event.type === 'finish') {
                  safeResolve()
                }
                else if (event.type === 'error') {
                  safeReject(event.error)
                }
              }
              catch (error) {
                safeReject(error)
              }
            },
          },
        ).catch(safeReject)
      })
    }
    catch (error) {
      console.warn(`❌ ${contact.name}: LLM 调用失败（不更新 weights，下次按真实间隔重试）:`, error)
      return outcome
    }

    // ===== 🔧 工具调用已在 stream 中自动执行 =====
    // 工具执行结果已经通过 buildMomentsSimulationTools 的 execute 回调写入 postsStore
    // 这里只需要根据工具调用情况更新 outcome 状态

    // 如果 stream 返回了任何内容或工具被调用，至少升级到 silent
    // （stream 在工具调用时会触发 'tool-call' 事件，onActionExecuted 会更新 outcome）
    if (outcome === 'failed' && fullResponse.trim()) {
      // 如果没有工具调用但有文本响应，升级为 silent
      outcome = 'silent'
    }

    // 根据 outcome 更新 weights：
    //   posted → 真实发帖时间戳，正常 8h 衰减冷却
    //   silent → 时间戳设为 4h 前，约 4h 后概率恢复 ~39%（让爱沉默的角色让位但不消失）
    //   failed → 不动 weights，让概率随真实发帖间隔自然恢复，技术故障不污染冷却
    // 注意：outcome 可能在 onActionExecuted 回调中被设置为 'posted'，TypeScript 无法追踪这个动态赋值
    const finalOutcome = outcome as 'posted' | 'silent' | 'failed'
    if (finalOutcome === 'posted') {
      contactPostWeights.value = {
        ...contactPostWeights.value,
        [contact.id]: Date.now(),
      }
    }
    else if (finalOutcome === 'silent') {
      contactPostWeights.value = {
        ...contactPostWeights.value,
        [contact.id]: Date.now() - 4 * 60 * 60 * 1000,
      }
    }

    return finalOutcome
  }

  /**
   * 触发朋友圈后台模拟
   * 🎲 遍历所有联系人，按权重随机选择谁来发动态
   * 最近发过的联系人概率降低，保证多样性
   * @param maxPosts 本次模拟最多允许几个联系人发动态（默认 MAX_POSTS_PER_SIMULATION）
   */
  let isMomentsSimulating = false
  // 跨页签互斥：localStorage 时间戳锁，最长持有 1 分钟（防止异常崩溃后永久卡死）
  // isMomentsSimulating 是页签内变量挡不住跨页签竞争，所以两层都需要
  const momentsSimulationLockUntil = useLocalStorage<number>('background-activity/moments-simulation-lock', 0)
  const MOMENTS_SIMULATION_LOCK_TTL_MS = 60 * 1000

  // LLM 连续故障退避：每次完整 'failed' 模拟后 +1，成功（产出或沉默）则归零
  // lastMomentsFailureTime：最后一次连续失败的时间戳，独立于 lastMomentsCheck 以确保退避真实生效
  // （lastMomentsCheck 在每次尝试末尾都刷新，无法用于退避计时）
  // ⭐ 持久化到 localStorage：手机 PWA 后台杀进程或刷新会让模块变量归零，
  // 不持久化时 4h 退避在生产环境几乎从未真正生效
  const consecutiveFailures = useLocalStorage('background-activity/moments-consecutive-failures', 0)
  const lastMomentsFailureTime = useLocalStorage('background-activity/moments-last-failure-time', 0)

  async function triggerMomentsSimulation(maxPosts?: number) {
    if (isMomentsSimulating) {
      console.info('🎯 朋友圈模拟已在进行中（同页签），跳过')
      return
    }
    const now = Date.now()
    // 🛡️ 运行时时钟回拨防护：lock 大于 now + TTL 视为时钟被回拨，主动清零防永久卡死
    if (momentsSimulationLockUntil.value > now + MOMENTS_SIMULATION_LOCK_TTL_MS) {
      console.warn('⚠️ 检测到 momentsSimulationLockUntil 异常超出 TTL（设备时间被回拨？），清零')
      momentsSimulationLockUntil.value = 0
    }
    if (now < momentsSimulationLockUntil.value) {
      console.info('🎯 朋友圈模拟已在其他页签进行中，跳过')
      return
    }
    // 🛡️ LLM 连续故障退避：连续 ≥3 次失败后启用，按指数退避（30min/60min/120min/...，最多 30min × 因子）
    // 使用独立的 lastMomentsFailureTime 计时，因为 lastMomentsCheck 在每次尝试末尾都刷新会让退避形同虚设
    if (consecutiveFailures.value >= 3 && lastMomentsFailureTime.value > 0) {
      // 起步 30min，每多一次失败翻倍，最大 240min（4h）
      // 选 30min 起步是因为它必须 ≥ MOMENTS_RECHECK_MIN_MS，否则会被防刷屏门槛吞掉
      const backoffMs = Math.min(
        4 * 60 * 60 * 1000,
        30 * 60 * 1000 * (2 ** Math.min(consecutiveFailures.value - 3, 4)),
      )
      const sinceFailure = now - lastMomentsFailureTime.value
      if (sinceFailure < backoffMs) {
        console.info(`⏸️ 已连续失败 ${consecutiveFailures.value} 次，退避中（剩余 ${((backoffMs - sinceFailure) / 60000).toFixed(1)}min）`)
        // 退避命中时同步刷新 lastMomentsCheck，避免被 30min 防刷屏穿透（让两层叠加而非互相替代）
        lastMomentsCheck.value = now
        return
      }
    }

    // 跨页签互斥（乐观锁，非真正原子）：写入 TTL 时间戳，60s 超时自动失效。
    // 注意：useLocalStorage 跨 tab 同步靠 storage 事件（异步宏任务），本页签内 .value
    // 是内存 ref，写后立即读取仍是自身值。两个 tab 几乎同时进入这段会双双通过检查，
    // 各自跑一次模拟。真正的"双倍贴文"由 contact 概率筛选 + maxPosts 自然稀释，
    // 业务上幂等损失可接受；如果未来发现是问题，再升级为 navigator.locks 或 BroadcastChannel 仲裁。
    momentsSimulationLockUntil.value = now + MOMENTS_SIMULATION_LOCK_TTL_MS
    isMomentsSimulating = true
    console.info('🎯 ===== 朋友圈后台模拟触发 =====')

    const limit = maxPosts ?? MAX_POSTS_PER_SIMULATION

    try {
      if (!sendOptions.value) {
        console.warn('❌ 未配置sendOptions，无法生成朋友圈')
        return
      }

      const providerReady = await ensureChatProvider()
      if (!providerReady) {
        console.warn('❌ chatProvider 未就绪，跳过朋友圈模拟')
        return
      }

      const { usePostsStore } = await import('./posts')
      const { useUserPersonaStore } = await import('./user-persona')
      const { useLLM } = await import('./llm')

      const postsStore = usePostsStore()
      const userPersonaStore = useUserPersonaStore()
      const { stream: streamFn } = useLLM()

      // 获取所有非群聊联系人，排除被完全拉黑的（双向 blocked_*）
      const allContacts = (await contactsStore.getAllContacts())
        .filter((c: unknown) => !(c as { isGroup?: boolean }).isGroup)
        .filter((c: unknown) => !contactsStore.isFullyBlocked((c as { id: string }).id))

      if (allContacts.length === 0) {
        console.warn('❌ 没有联系人')
        return
      }

      const allContactNames = allContacts.map(c => c.name)
      const allContactIds = allContacts.map(c => c.id)
      const userNickname = userPersonaStore.userName || '用户'

      // 🎲 为每个联系人计算发布概率，随机选择
      const candidates: Array<{ contact: typeof allContacts[0], probability: number }> = []

      for (const contact of allContacts) {
        const probability = getContactPostProbability(contact.id)
        candidates.push({ contact, probability })
        const lastPost = contactPostWeights.value[contact.id]
        const lastPostInfo = lastPost
          ? `${((Date.now() - lastPost) / (1000 * 60 * 60)).toFixed(1)}h前`
          : '从未'
        console.info(`  🎲 ${contact.name}: 概率 ${(probability * 100).toFixed(0)}% (上次发布: ${lastPostInfo})`)
      }

      // 按概率随机筛选，打乱顺序增加随机性
      const shuffled = candidates.sort(() => Math.random() - 0.5)
      const selected: typeof allContacts = []

      for (const { contact, probability } of shuffled) {
        if (selected.length >= limit)
          break
        if (Math.random() < probability) {
          selected.push(contact)
        }
      }

      // 如果一个都没选中但离线时间很长（>12h），强制选一个概率最高的
      // 用 lastMomentsProduced 而非 lastMomentsCheck —— 否则空跑刷新 lastMomentsCheck 会让兜底永远走不到
      if (selected.length === 0) {
        const timeSinceProduced = Date.now() - lastMomentsProduced.value
        const hoursSinceProduced = timeSinceProduced / (1000 * 60 * 60)

        if (hoursSinceProduced > 12) {
          const best = candidates.sort((a, b) => b.probability - a.probability)[0]
          if (best) {
            selected.push(best.contact)
            console.info(`⚡ 距上次产出已 ${hoursSinceProduced.toFixed(1)}h（>12h），强制选择 ${best.contact.name} 发布动态`)
          }
        }
        else {
          console.info('🤷 本次模拟没有联系人被选中发布动态（正常现象）')
        }
      }

      console.info(`✅ 本次选中 ${selected.length} 个联系人参与朋友圈模拟: ${selected.map(c => c.name).join(', ') || '无'}`)

      // 为每个联系人生成不同的回溯时间戳（离线期间的随机时间）
      // 🕐 使用时段感知算法：避开深夜，聚集在活跃时段
      // 这里重新取一次时间（不复用入口处的 now）：从入口到这里可能已过去几秒，
      // 用更精确的时间能让生成的回溯时间戳更贴近"现在"
      const generationStart = Date.now()
      const timeSinceLastCheck = generationStart - lastMomentsCheck.value
      // 限制回溯范围：最多回溯 48 小时，防止首次使用或数据异常时生成过于久远的时间戳
      const maxBackdateMs = 48 * 60 * 60 * 1000
      const effectiveSpan = Math.min(timeSinceLastCheck, maxBackdateMs)
      const spanStart = generationStart - effectiveSpan

      const backdatedTimestamps = selected.map(() => {
        return generateNaturalTimestamp(spanStart, generationStart)
      })
      // 按时间排序，确保帖子顺序自然
      backdatedTimestamps.sort((a, b) => a - b)

      // 依次为选中的联系人执行模拟
      let producedCount = 0
      let failedCount = 0
      let okCount = 0 // 'silent' 或 'posted' 都算成功（LLM 链路 OK）
      for (let i = 0; i < selected.length; i++) {
        const contact = selected[i]
        try {
          // TypeScript 无法追踪 simulateMomentsForContact 内部回调中的动态赋值
          const outcome = await simulateMomentsForContact(
            contact,
            allContactNames,
            allContactIds,
            postsStore,
            userNickname,
            streamFn,
            backdatedTimestamps[i], // 传递回溯时间戳
          ) as 'posted' | 'silent' | 'failed'
          if (outcome === 'posted') {
            producedCount++
            okCount++
          }
          else if (outcome === 'silent') {
            okCount++
          }
          else {
            failedCount++
          }
          // 联系人之间添加随机延迟（1-3秒），避免请求过快
          if (i < selected.length - 1) {
            await new Promise(resolve => workerTimer.setTimeout(() => resolve(undefined), 1000 + Math.random() * 2000))
          }
        }
        catch (error) {
          // 异常被吞掉但仍要计入失败，否则 LLM throw 异常的轮次反而不会触发退避计数
          failedCount++
          console.error(`❌ ${contact.name} 朋友圈模拟失败:`, error)
        }
      }

      // 更新最后检查时间（用于 30min 防刷屏 / 调度节奏）
      lastMomentsCheck.value = Date.now()
      // 仅在真正产出动态时刷新 lastMomentsProduced(兜底/超期触发依赖这个时间戳)
      if (producedCount > 0)
        lastMomentsProduced.value = Date.now()

      // 退避计数：本轮全失败则累加 + 刷新 lastMomentsFailureTime；任一成功（含沉默）则归零
      // 'selected.length === 0' 不算失败也不算成功，不动计数
      if (selected.length > 0) {
        if (okCount === 0 && failedCount > 0) {
          consecutiveFailures.value++
          lastMomentsFailureTime.value = Date.now()
          if (consecutiveFailures.value >= 3)
            console.warn(`⚠️ 朋友圈模拟连续失败 ${consecutiveFailures.value} 次，下次将退避（避免烧 token）`)
        }
        else if (okCount > 0) {
          if (consecutiveFailures.value > 0)
            console.info(`✅ 朋友圈模拟恢复正常（之前连续失败 ${consecutiveFailures.value} 次）`)
          consecutiveFailures.value = 0
          lastMomentsFailureTime.value = 0
        }
      }

      console.debug('✅ 朋友圈模拟完成')
    }
    catch (error) {
      console.error('❌ 朋友圈模拟失败:', error)
    }
    finally {
      isMomentsSimulating = false
      momentsSimulationLockUntil.value = 0
    }
  }

  /**
   * 检查是否需要运行朋友圈模拟（离线后回来时调用）
   *
   * 双门槛设计：
   * - lastMomentsCheck（30min 防刷屏）：避免反复切前台导致 token 浪费
   * - lastMomentsProduced（2h 业务必要性）：仅在距上次真正产出已超 2h 时才触发
   *
   * 首次启用时 lastMomentsProduced=0，sinceProduced 是巨大值 → 必然通过门槛 2 → 触发
   */
  async function checkAndRunMomentsSimulation() {
    if (!enabled.value) {
      return
    }

    if (isQuietTime()) {
      console.info('🌙 安静时间内，跳过离线朋友圈模拟')
      return
    }

    const sinceCheck = Date.now() - lastMomentsCheck.value
    if (sinceCheck < MOMENTS_RECHECK_MIN_MS) {
      console.debug(`⏭️ ${(sinceCheck / 60000).toFixed(0)}min 前刚尝试过朋友圈模拟，跳过避免刷屏`)
      return
    }

    const sinceProduced = Date.now() - lastMomentsProduced.value
    const hoursSinceProduced = sinceProduced / (1000 * 60 * 60)

    if (hoursSinceProduced >= MOMENTS_MIN_OFFLINE_HOURS) {
      console.info(`⏰ 距上次产出 ${hoursSinceProduced.toFixed(1)} 小时，触发朋友圈模拟`)
      await triggerMomentsSimulation()
    }
    else {
      console.debug(`⏭️  距上次产出仅 ${hoursSinceProduced.toFixed(1)} 小时，未达到阈值 ${MOMENTS_MIN_OFFLINE_HOURS} 小时`)
    }
  }

  /**
   * ⏰ 定期朋友圈模拟调度（高频低量策略）
   * 每 1~2 小时（±30min 随机抖动）检查一次，每次最多 1 个联系人发帖
   * 使用自调度实现随机间隔，避免固定周期的机械感
   *
   * 关键不变量：
   * - timer 必须在 await LLM 之前排程，否则手机后台冻结 await 会让调度链永久死锁
   * - 立即触发用 fire-and-forget（不 await），保证调度链不被 LLM 挂起阻塞
   * - 入口检查 enabled，防止 stop() 后回调链复活
   */
  function scheduleMomentsCheck() {
    if (!enabled.value) {
      // stop 后绝不重启调度
      return
    }
    if (isSchedulingMoments)
      return
    isSchedulingMoments = true
    try {
      if (momentsPeriodicTimer) {
        workerTimer.clearTimeout(momentsPeriodicTimer)
        momentsPeriodicTimer = null
      }

      // 计算下次间隔（不依赖 provider 状态，永远使用基础抖动）
      // 旧逻辑：providerPending 时缩短到 2min，但因为我们改成 fire-and-forget，
      // provider 检测在异步分支里，主调度循环不需要等待它
      const interval = MOMENTS_CHECK_BASE_MS + (Math.random() - 0.5) * 2 * MOMENTS_CHECK_JITTER_MS

      // 持久化下次预计触发时间，便于 app 重启 / 后台杀进程后恢复
      savedMomentsCheckEndTime.value = Date.now() + interval

      // ⭐ 优先排程下次 timer！这样即使下面的立即触发 await 挂起（手机后台冻结、LLM 慢响应），
      // 调度链也已经续上，不会再陷入"timer 永远不被排程"的死锁
      momentsPeriodicTimer = workerTimer.setTimeout(() => {
        // 触发后立即递归排程下次（fire-and-forget），与 await LLM 解耦
        scheduleMomentsCheck()
        if (!enabled.value || isQuietTime())
          return
        ensureChatProvider().then((providerReady) => {
          if (!providerReady)
            return
          console.info('⏰ 定期朋友圈模拟触发')
          triggerMomentsSimulation(1).catch(error =>
            console.error('❌ 定期朋友圈模拟失败:', error),
          )
        }).catch(error => console.error('❌ provider 检测失败:', error))
      }, interval)

      // 检查是否已超期，超期则立即触发（解决用户短暂上线场景）
      // 双门槛：lastMomentsCheck 防 30min 内刷屏，lastMomentsProduced 控 90min 业务节奏
      const sinceCheck = Date.now() - lastMomentsCheck.value
      const sinceProduced = Date.now() - lastMomentsProduced.value
      if (sinceCheck >= MOMENTS_RECHECK_MIN_MS && sinceProduced >= MOMENTS_CHECK_BASE_MS && !isQuietTime()) {
        // ⭐ fire-and-forget：不 await，让本函数立即返回释放 isSchedulingMoments，
        // 防止 LLM 长响应或后台冻结造成 schedule 链锁死
        ensureChatProvider().then((providerReady) => {
          if (!providerReady) {
            console.info('⏳ 服务商未就绪，等待下次自然周期')
            return
          }
          const offlineHours = sinceProduced / (1000 * 60 * 60)
          const maxPosts = offlineHours >= MOMENTS_MIN_OFFLINE_HOURS ? undefined : 1
          console.info(`⏰ 距上次产出已 ${(sinceProduced / 60000).toFixed(0)}min（${offlineHours.toFixed(1)}h），立即触发（最多 ${maxPosts ?? MAX_POSTS_PER_SIMULATION} 条）`)
          triggerMomentsSimulation(maxPosts).catch(error =>
            console.error('❌ 超期朋友圈模拟失败:', error),
          )
        }).catch(error => console.error('❌ provider 检测失败:', error))
      }
    }
    finally {
      isSchedulingMoments = false
    }
  }

  /**
   * 🔄 从 localStorage 恢复定期朋友圈调度
   *
   * 关键修复对象：调度链曾因为 await 挂起 + 手机后台冻结导致永久死锁
   * （saved=null + 没有 timer + 没有调度循环），用户重启后调度可能仍然不工作。
   *
   * 恢复策略：
   * - saved=null 但 enabled=true → 调度链丢失，强制重置 schedule 锁并由 start() 续上的 scheduleMomentsCheck() 接管
   * - saved 在过去 → 标记需要立即重启（清空 saved，scheduleMomentsCheck 按双门槛判断）
   * - saved 在未来 → 仍由 scheduleMomentsCheck() 重新设置 timer 接管
   */
  function recoverMomentsCheck() {
    if (!enabled.value)
      return
    // 恢复时一并复位调度锁/模拟锁，避免 app 上次崩溃留下的锁卡死下一轮调度
    isSchedulingMoments = false
    isMomentsSimulating = false
    // 复位跨页签锁：app 上次崩溃可能留下未释放的 TTL 时间戳
    // start() 的时钟回拨自愈也会处理这个，但崩溃后 60s 内重启会被旧 TTL 挡住，主动清零最稳
    momentsSimulationLockUntil.value = 0

    const saved = savedMomentsCheckEndTime.value
    if (!saved) {
      console.info('🔄 未检测到 savedMomentsCheckEndTime（可能后台杀进程或首次启用），将由 scheduleMomentsCheck 重新建立调度链')
      return
    }
    if (saved <= Date.now()) {
      console.info('🔄 检测到过期的朋友圈检查时间，立即重启调度')
      savedMomentsCheckEndTime.value = null
      // scheduleMomentsCheck 内部会按双门槛判断是否立即触发
    }
  }

  /**
   * 辅助函数：调用LLM生成评论（增强版 - 包含记忆、关系、聊天历史等上下文）
   */
  async function generateAIComment(
    contactId: string,
    contactName: string,
    postContent: string,
    isMentioned: boolean,
    imageUrl?: string,
    imageDescription?: string,
    contextType: 'post' | 'comment_reply' = 'post',
  ): Promise<string | null> {
    const providerReady = await ensureChatProvider()
    if (!providerReady || !sendOptions.value?.model) {
      console.error('❌ 无法生成AI评论：LLM 配置未就绪')
      return null
    }

    // 获取联系人对应的角色卡人设
    const contact = contactsStore.getContact(contactId)
    let characterPersona = ''

    if (contact && contact.cardId) {
      const card = airiCardStore.getCard(contact.cardId)
      if (card) {
        // 组合角色卡的人设信息
        const components = [
          card.systemPrompt,
          card.description,
          card.personality,
        ].filter(Boolean)
        characterPersona = components.join('\n')
      }
    }

    // ===== 🧠 增强上下文：记忆系统 =====
    const timeAware = contactsStore.getContact(contactId)?.timeAwarenessEnabled !== false
    let memoryContext = ''
    try {
      const { useMemoryStore } = await import('./memory')
      const memoryStore = useMemoryStore()

      // 临时切换到该联系人的记忆上下文
      const previousContactId = memoryStore.currentContactId
      memoryStore.setCurrentContact(contactId)

      // 获取与动态内容相关的记忆（关键词检索）
      const searchResult = memoryStore.retrieveRelevantMemories(postContent)

      // 获取最近的对话摘要（最多3条）
      const recentSummaries = searchResult.summaries.slice(0, 3)
      if (recentSummaries.length > 0) {
        const summaryTexts = recentSummaries.map((s: MemorySummary) => {
          return timeAware
            ? `- (${memoryStore.formatRelativeTime(s.endTime)}) ${s.content}`
            : `- ${s.content}`
        }).join('\n')
        memoryContext += `\n## 与用户的对话记忆\n${summaryTexts}\n`
      }

      // 获取重要记忆（最多5条）
      const relevantMemories = searchResult.memories.slice(0, 5)
      if (relevantMemories.length > 0) {
        const memoryTexts = relevantMemories.map((m: MemoryItem) => {
          const tag = m.isImportant ? '[核心]' : ''
          return timeAware
            ? `- ${tag}(${memoryStore.formatRelativeTime(m.timestamp)}) ${m.content}`
            : `- ${tag}${m.content}`
        }).join('\n')
        memoryContext += `\n## 关键记忆\n${memoryTexts}\n`
      }

      // 恢复之前的联系人上下文
      if (previousContactId) {
        memoryStore.setCurrentContact(previousContactId)
      }
    }
    catch (error) {
      console.warn('获取记忆上下文失败:', error)
    }

    // ===== 💕 增强上下文：关系系统 =====
    let relationshipContext = ''
    try {
      const { useRelationshipStore } = await import('./relationship')
      const relationshipStore = useRelationshipStore()
      const relationship = relationshipStore.getRelationship(contactId)

      if (relationship) {
        const description = relationshipStore.getRelationshipDescription(contactId)
        relationshipContext = `\n## 与用户的关系\n${description}\n`
      }
    }
    catch (error) {
      console.warn('获取关系上下文失败:', error)
    }

    // ===== 💬 增强上下文：最近聊天记录 =====
    let chatContext = ''
    try {
      const recentMessages = chatStore.getMessagesForContact(contactId)
      if (recentMessages && recentMessages.length > 0) {
        // 取最近20条非系统消息
        const lastMessages = recentMessages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .slice(-20)
        if (lastMessages.length > 0) {
          const chatTexts = lastMessages.map((m) => {
            const role = m.role === 'user' ? '用户' : contactName
            const rawContent = typeof m.content === 'string' ? m.content : ''
            const content = rawContent.substring(0, 80)
            return `- ${role}: ${content}${rawContent.length > 80 ? '...' : ''}`
          }).join('\n')
          chatContext = `\n## 最近的私聊记录\n${chatTexts}\n`
        }
      }
    }
    catch (error) {
      console.warn('获取聊天记录失败:', error)
    }

    // ===== 📞 增强上下文：通话记录 =====
    let callContext = ''
    try {
      const recentCalls = callStore.getCallLogsByCharacter(contactId)
      if (recentCalls && recentCalls.length > 0) {
        const lastCalls = recentCalls.slice(0, 3)
        const callTexts = lastCalls.map((log: any) => {
          const duration = log.duration ? `${Math.round(log.duration / 60)}分钟` : '未接通'
          const typeText = log.type === 'video' ? '视频' : '语音'
          return timeAware
            ? `- ${new Date(log.startTime).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} ${typeText}通话 (${duration})`
            : `- ${typeText}通话 (${duration})`
        }).join('\n')
        callContext = `\n## 最近的通话记录\n${callTexts}\n`
      }
    }
    catch (error) {
      console.warn('获取通话记录失败:', error)
    }

    const currentTime = timeAware
      ? new Date().toLocaleString('zh-CN', {
          dateStyle: 'full',
          timeStyle: 'short',
        })
      : ''

    // ===== 构建增强版 Prompt =====
    const hasContext = memoryContext || relationshipContext || chatContext || callContext
    const contextSection = hasContext
      ? `\n# 你与用户的背景信息（请自然地利用这些信息，让评论更有温度）\n${memoryContext}${relationshipContext}${chatContext}${callContext}`
      : ''

    // ===== 🖼️ 图片上下文处理 =====
    const hasImage = !!imageUrl
    let imageContextText = ''
    if (hasImage && imageDescription) {
      imageContextText = `\n（动态附带了一张图片，图片描述: ${imageDescription}）`
    }
    else if (hasImage) {
      imageContextText = '\n（动态附带了一张图片）'
    }

    const currentSituation = contextType === 'comment_reply'
      ? `${isMentioned ? '用户在朋友圈评论区 @了你，' : '你看到用户在朋友圈评论区'}发表了一条评论/回复。`
      : `${isMentioned ? '用户在朋友圈 @了你，' : '你看到用户在朋友圈'}发布了一条动态。`
    const contentLabel = contextType === 'comment_reply' ? '评论区上下文' : '动态内容'
    const taskText = contextType === 'comment_reply'
      ? '请用简短、自然的方式回复用户这条评论（1-2句话），体现你的性格。'
      : '请用简短、自然的方式回复（1-2句话），体现你的性格。'

    const commentPrompt = `你是 ${contactName}。

# 你的人设
${characterPersona || '一个友好的AI助手'}
${contextSection}
${timeAware ? `# 当前时间\n${currentTime}\n` : ''}
# 当前情况
${currentSituation}

${contentLabel}: ${postContent}${imageContextText}

# 你的任务
${taskText}
- 如果你和用户之间有相关的记忆或最近的聊天话题，可以自然地关联
- 评论要符合你们当前的关系亲密度
${hasImage ? '- 如果动态包含图片，请结合图片内容进行评论\n' : ''}- 请直接回复评论内容，不要包含任何额外格式、标记或JSON`

    // ===== 构建消息（支持多模态） =====
    const systemMessage = { role: 'system' as const, content: commentPrompt }

    // 尝试多模态：如果有图片且是 base64 格式，先尝试以 vision 方式发送
    // 超过阈值的图片先压缩再发送，压缩后仍过大则跳过多模态
    const MULTIMODAL_COMPRESS_THRESHOLD = 200 * 1024
    const MULTIMODAL_MAX_SIZE = 500 * 1024
    let processedImageUrl = imageUrl
    let tryMultimodal = hasImage && imageUrl && imageUrl.startsWith('data:')

    if (tryMultimodal && imageUrl && imageUrl.length > MULTIMODAL_COMPRESS_THRESHOLD) {
      try {
        const { compressDataUrlViaCanvas } = await import('./chat-helpers')
        const compressed = await compressDataUrlViaCanvas(imageUrl, 512)
        if (compressed.length < imageUrl.length) {
          console.info(`🖼️ 图片已压缩: ${Math.round(imageUrl.length / 1024)}KB → ${Math.round(compressed.length / 1024)}KB`)
          processedImageUrl = compressed
        }
        if (processedImageUrl!.length > MULTIMODAL_MAX_SIZE) {
          console.info(`🖼️ 压缩后仍过大 (${Math.round(processedImageUrl!.length / 1024)}KB)，跳过多模态`)
          tryMultimodal = false
        }
      }
      catch (error) {
        console.warn('⚠️ 图片压缩失败，跳过多模态:', error)
        tryMultimodal = false
      }
    }

    /**
     * 内部调用 LLM 的辅助函数
     */
    const callLLM = async (messages: any[]): Promise<string> => {
      // 同 simulateMomentsForContact：必须有硬超时，否则坏 stream 让外层 await 永久卡住
      // 同时 idle-timeout 区分"慢但正常"和"完全没响应"，思考模型友好
      const LLM_HARD_TIMEOUT_MS = 180 * 1000
      const LLM_IDLE_TIMEOUT_MS = 90 * 1000
      let fullResponse = ''
      await new Promise<void>((resolve, reject) => {
        let settled = false
        let idleTimer: number | null = null
        let hardTimerId: number = 0
        const safeResolve = () => {
          if (settled)
            return
          settled = true
          workerTimer.clearTimeout(hardTimerId)
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          resolve()
        }
        const safeReject = (err: unknown) => {
          if (settled)
            return
          settled = true
          workerTimer.clearTimeout(hardTimerId)
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          reject(err)
        }
        const resetIdleTimer = () => {
          if (settled)
            return
          if (idleTimer !== null)
            workerTimer.clearTimeout(idleTimer)
          idleTimer = workerTimer.setTimeout(() => {
            safeReject(new Error(`LLM stream 空闲 ${LLM_IDLE_TIMEOUT_MS / 1000}s 无新增字节`))
          }, LLM_IDLE_TIMEOUT_MS)
        }
        hardTimerId = workerTimer.setTimeout(() => {
          safeReject(new Error(`LLM stream 总时长超过 ${LLM_HARD_TIMEOUT_MS / 1000}s 仍未完成`))
        }, LLM_HARD_TIMEOUT_MS)
        resetIdleTimer()

        stream(
          sendOptions.value!.model,
          sendOptions.value!.chatProvider!,
          messages,
          {
            headers: sendOptions.value!.providerConfig?.headers || {},
            async onStreamEvent(event) {
              if (event.type === 'text-delta' && event.text) {
                fullResponse += event.text
                resetIdleTimer()
              }
              else if (event.type === 'finish') {
                safeResolve()
              }
              else if (event.type === 'error') {
                safeReject(event.error)
              }
            },
          },
        ).catch(safeReject)
      })
      return fullResponse.trim()
    }

    const MAX_COMMENT_RETRIES = 2
    for (let attempt = 0; attempt <= MAX_COMMENT_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          console.warn(`🔄 AI评论重试 (${attempt}/${MAX_COMMENT_RETRIES})...`)
          await ensureChatProvider()
          await new Promise(r => workerTimer.setTimeout(() => r(undefined), 2000))
        }

        if (tryMultimodal) {
          // 🖼️ 多模态模式：将图片作为 vision content 发送
          console.info(`🖼️ 尝试多模态评论（图片大小: ${Math.round(processedImageUrl!.length / 1024)}KB）`)
          const multimodalUserMessage = {
            role: 'user' as const,
            content: [
              { type: 'text' as const, text: '请看这张图片并回复评论。' },
              { type: 'image_url' as const, image_url: { url: processedImageUrl! } },
            ],
          }

          try {
            const result = await callLLM([systemMessage, multimodalUserMessage])
            if (result) {
              console.info('✅ 多模态评论成功')
              return result
            }
          }
          catch (multimodalError) {
            console.warn('⚠️ 多模态调用失败，降级为纯文本模式:', multimodalError)
            tryMultimodal = false
          }
        }

        // 📝 纯文本模式（默认 / 多模态降级后的 fallback）
        const textUserMessage = { role: 'user' as const, content: '请回复评论。' }
        const result = await callLLM([systemMessage, textUserMessage])
        if (result)
          return result
      }
      catch (error) {
        if (attempt === MAX_COMMENT_RETRIES) {
          console.error('❌ LLM调用失败（已耗尽重试）:', error)
          return null
        }
        console.warn(`⚠️ AI评论生成失败 (尝试 ${attempt + 1}/${MAX_COMMENT_RETRIES + 1}):`, error)
      }
    }
    return null
  }

  /**
   * 用户发送朋友圈后，触发AI响应
   * @param postId 动态ID
   * @param mentionIds 被@的联系人ID列表
   * @param visibleTo 可见联系人ID列表（空表示公开）
   */
  async function triggerAIResponseToUserPost(
    postId: string,
    mentionIds: string[] = [],
    visibleTo: string[] = [],
  ) {
    console.info('🎯 ===== 用户发布动态，触发AI响应 =====')
    console.info('📝 动态ID:', postId)
    console.info('👥 @提及:', mentionIds.length > 0 ? mentionIds : '无')
    console.info('👁️  可见范围:', visibleTo.length > 0 ? `指定联系人 (${visibleTo.length}人)` : '所有人')

    try {
      // 排除群聊和被完全拉黑的联系人
      const allContacts = (await contactsStore.getAllContacts())
        .filter((c: any) => !c.isGroup)
        .filter((c: any) => !contactsStore.isFullyBlocked(c.id))

      // 确定需要响应的联系人
      let respondingContacts = []

      if (visibleTo.length > 0) {
        // 有指定可见联系人
        respondingContacts = allContacts.filter(c => visibleTo.includes(c.id))
      }
      else {
        // 公开动态，所有联系人都可见
        respondingContacts = allContacts
      }

      console.info(`👀 可见联系人: ${respondingContacts.length}人`)

      // 被@的联系人必须响应
      const mentionedContacts = respondingContacts.filter(c => mentionIds.includes(c.id))
      const otherVisibleContacts = respondingContacts.filter(c => !mentionIds.includes(c.id))

      console.info(`📢 必须响应（被@）: ${mentionedContacts.length}人`)
      console.info(`🤔 可能响应（未被@）: ${otherVisibleContacts.length}人`)

      // 1. 被@的联系人必须响应（点赞或评论），但加入关系感知延迟
      for (const contact of mentionedContacts) {
        console.info(`💬 处理被@的联系人: ${contact.name}`)

        // ⏱️ 关系感知延迟：亲密的人回复更快
        const affection = await getContactAffection(contact.id)

        // 🙄 负好感度联系人可能无视 @
        if (affection < -30 && Math.random() < 0.20) {
          console.info(`  🙄 ${contact.name} (好感度: ${affection}) 无视了你的@`)
          continue
        }

        let delay = calculateResponseDelay(affection)
        // 🌙 安静时间内根据好感度追加延迟
        if (isQuietTime()) {
          delay += getQuietTimeDelay(affection)
        }
        const delayMinutes = (delay / 60000).toFixed(1)
        console.info(`  ⏱️ ${contact.name} (好感度: ${affection}) 将在 ${delayMinutes} 分钟后响应`)

        const pendingResponse: PendingMomentsResponse = {
          id: `mentioned-${postId}-${contact.id}`,
          postId,
          contactId: contact.id,
          contactName: contact.name,
          triggerTime: Date.now() + delay,
          type: 'mentioned',
          affection,
        }
        savePendingResponse(pendingResponse)
        schedulePendingResponse(pendingResponse, delay)
      }

      // 2. 其他可见联系人：基于关系亲密度决定是否响应及响应方式
      for (const contact of otherVisibleContacts) {
        // 💕 获取关系亲密度，动态计算响应概率和方式
        const affection = await getContactAffection(contact.id)
        const responseConfig = getRelationshipResponseConfig(affection)

        const shouldRespond = Math.random() < responseConfig.respondProbability

        if (!shouldRespond) {
          console.info(`  ⏭️  ${contact.name} (好感度: ${affection}, 响应率: ${(responseConfig.respondProbability * 100).toFixed(0)}%) 看到了但没有响应`)
          continue
        }

        console.info(`💬 处理其他可见联系人: ${contact.name} (好感度: ${affection})`)

        // ⏱️ 关系感知延迟
        let delay = calculateResponseDelay(affection)
        // 🌙 安静时间内根据好感度追加延迟
        if (isQuietTime()) {
          delay += getQuietTimeDelay(affection)
        }
        const delayMinutes = (delay / 60000).toFixed(1)
        console.info(`  ⏱️ ${contact.name} 将在 ${delayMinutes} 分钟后响应`)

        const pendingResponse: PendingMomentsResponse = {
          id: `visible-${postId}-${contact.id}`,
          postId,
          contactId: contact.id,
          contactName: contact.name,
          triggerTime: Date.now() + delay,
          type: 'visible',
          affection,
        }
        savePendingResponse(pendingResponse)
        schedulePendingResponse(pendingResponse, delay)
      }

      console.debug('✅ AI响应完成')
    }
    catch (error) {
      console.error('❌ AI响应失败:', error)
    }
  }

  /**
   * 用户评论动态后，触发AI回复
   * @param postId 动态ID
   * @param commentText 用户的评论内容
   * @param mentionIds 评论中@的联系人ID列表
   * @param userCommentId 用户刚发表的评论ID
   */
  async function triggerAIResponseToUserComment(
    postId: string,
    commentText: string,
    mentionIds: string[] = [],
    userCommentId?: string,
  ) {
    console.info('💬 ===== 用户评论动态，触发AI回复 =====')
    console.info('📝 动态ID:', postId)
    console.info('💭 评论内容:', commentText)
    console.info('👥 @提及:', mentionIds.length > 0 ? mentionIds : '无')

    try {
      const postsStore = usePostsStore()
      const post = postsStore.getPostById(postId)

      if (!post) {
        console.error('❌ 找不到动态')
        return
      }

      // 排除群聊和被完全拉黑的联系人
      const allContacts = (await contactsStore.getAllContacts())
        .filter((c: any) => !c.isGroup)
        .filter((c: any) => !contactsStore.isFullyBlocked(c.id))

      // 收集需要回复的联系人
      const replyingContacts: Array<{ contact: any, reason: string, probability: number }> = []

      for (const contact of allContacts) {
        // 1. 如果被@了，必须回复（100%）
        if (mentionIds.includes(contact.id)) {
          replyingContacts.push({
            contact,
            reason: '被@',
            probability: 1.0,
          })
          continue
        }

        // 2. 如果是该AI自己的动态，高概率回复（80%）
        if (post.authorId === contact.id) {
          replyingContacts.push({
            contact,
            reason: '自己的动态',
            probability: 0.8,
          })
          continue
        }

        // 3. 如果之前评论过这条动态，中等概率回复（40%）
        const hasCommentedBefore = post.comments.some(c => c.authorId === contact.id)
        if (hasCommentedBefore) {
          replyingContacts.push({
            contact,
            reason: '之前参与过',
            probability: 0.4,
          })
          continue
        }

        // 4. 如果动态@了这个AI（即使评论没@），低概率回复（20%）
        if (post.mentionIds?.includes(contact.id)) {
          replyingContacts.push({
            contact,
            reason: '动态@了我',
            probability: 0.2,
          })
        }
      }

      console.info(`🎯 潜在回复者: ${replyingContacts.length}人`)

      // 按概率决定谁实际回复
      for (const { contact, reason, probability } of replyingContacts) {
        const shouldReply = probability >= 1.0 || Math.random() < probability

        const userComment = [...post.comments].reverse().find(comment =>
          userCommentId
            ? comment.id === userCommentId
            : comment.authorId === 'user' && comment.text === commentText,
        )

        if (!shouldReply) {
          console.info(`  ⏭️  ${contact.name} (${reason}, ${(probability * 100).toFixed(0)}%) 决定不回复`)
          continue
        }

        console.info(`  💬 ${contact.name} (${reason}, ${(probability * 100).toFixed(0)}%) 准备回复...`)

        // 持久化待触发响应，确保 app 关闭后仍能恢复
        const pendingCommentKey = userComment?.id || `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
        const pendingId = `comment-${postId}-${contact.id}-${pendingCommentKey}`
        const isMentioned = mentionIds.includes(contact.id)
        savePendingResponse({
          id: pendingId,
          postId,
          contactId: contact.id,
          contactName: contact.name,
          triggerTime: Date.now(),
          type: 'comment_reply',
          affection: 0,
          userCommentText: commentText,
          userCommentId: userComment?.id,
          isMentioned,
        })

        try {
          // 构建回复prompt的上下文
          const isOwnPost = post.authorId === contact.id

          let contextDescription = ''
          if (isMentioned) {
            contextDescription = `用户在你的朋友圈动态下评论并@了你`
          }
          else if (isOwnPost) {
            contextDescription = `用户在你发布的朋友圈动态下评论了`
          }
          else {
            contextDescription = `用户在朋友圈动态下发表了评论`
          }

          const fullContext = `${contextDescription}。\n\n${userComment
            ? formatMomentsCommentThread(
                post,
                userComment,
                buildMomentsCommentThreadOptions(post, userComment, contact, contact.id, contactsStore, airiCardStore),
              )
            : `原动态内容: ${post.content || '（图片动态）'}\n\n用户的评论: ${commentText}`}`

          // 生成回复（传入图片数据以支持多模态）
          const replyText = await generateAIComment(
            contact.id,
            contact.name,
            fullContext,
            isMentioned,
            post.imageUrl,
            post.imageDescription,
            'comment_reply',
          )

          // 移除持久化记录
          removePendingResponse(pendingId)

          if (replyText) {
            postsStore.aiCommentOnPost(
              postId,
              replyText,
              contact.id,
              contact.name,
              userComment
                ? { id: userComment.id, author: userComment.author, authorId: userComment.authorId }
                : undefined,
            )
            console.info(`  ✅ ${contact.name} 回复: ${replyText.substring(0, 40)}...`)
          }
          else {
            console.error(`  ❌ ${contact.name} 回复失败`)
          }
        }
        catch (error) {
          removePendingResponse(pendingId)
          console.error(`  ❌ ${contact.name} 回复异常:`, error)
        }

        // 添加延迟，模拟真实回复时间
        await new Promise(resolve => workerTimer.setTimeout(() => resolve(undefined), 500 + Math.random() * 1500))
      }

      console.debug('✅ AI回复完成')
    }
    catch (error) {
      console.error('❌ AI回复失败:', error)
    }
  }

  /**
   * 心跳tick - 保持页面活跃
   *
   * 作用：
   * 1. 保持后台活跃：在移动端后台运行时，通过定期执行轻量级操作防止标签页被完全冻结
   * 2. 状态检查：定期检查是否需要执行某些延迟任务
   */
  const heartbeatTick = () => {
    if (!enabled.value) {
      return
    }

    // 前台时无需保活，直接跳过
    if (document.visibilityState !== 'hidden') {
      return
    }

    // 保活操作：更新时间戳到 LocalStorage（轻量级操作，防止移动端冻结标签页）
    localStorage.setItem('background-heartbeat-last-tick', Date.now().toString())
  }

  /**
   * 启动心跳
   * ⚙️ 使用 Web Worker 定时器，不受后台标签页节流影响
   */
  const startHeartbeat = () => {
    // 前台不需要保活心跳，避免 Worker 每分钟唤醒一次主线程。
    if (document.visibilityState !== 'hidden')
      return

    if (heartbeatIntervalId) {
      if (import.meta.env.DEV) {
        console.debug('ℹ️ 心跳已在运行，跳过重复启动')
      }
      return
    }

    console.debug(`💓 启动心跳，间隔: ${HEARTBEAT_INTERVAL}秒 (Worker: ${workerTimer.isAvailable ? '✅' : '❌降级主线程'})`)

    // ⚙️ 使用 Worker 定时器：后台不会被节流到 1min+
    heartbeatIntervalId = workerTimer.setInterval(() => {
      heartbeatTick()
      // 拉黑系统：扫描冷静期到期的联系人，触发 AI 申请加回
      scanBlockCooldowns().catch((e) => {
        console.warn('⚠️ 拉黑冷静期扫描失败:', e)
      })
    }, HEARTBEAT_INTERVAL * 1000)
  }

  /**
   * 停止心跳
   */
  const stopHeartbeat = () => {
    if (heartbeatIntervalId) {
      workerTimer.clearInterval(heartbeatIntervalId)
      heartbeatIntervalId = null
      console.info('⏸️  心跳已停止')
    }
  }

  // ============ 拉黑冷静期循环 ============

  // pending_system_reflection 状态的最大允许停留时间（5 分钟）
  // 超过则视为生成卡死，强制 fallback 退回 blocked_by_user
  const PENDING_REFLECTION_TIMEOUT_MS = 5 * 60 * 1000

  // 多 tab leader election：每个 tab 启动时生成唯一 ID，scanBlockCooldowns 启动前广播 claim
  // 监听 50ms 内有没有更早 timestamp 的 claim，没有就执行；有则让步
  const tabId = (() => {
    try {
      return crypto.randomUUID()
    }
    catch {
      return Math.random().toString(36).slice(2)
    }
  })()
  const competingClaims = ref<Array<{ timestamp: number, tabId: string }>>([])
  watch(receivedActivity, (event) => {
    if (event?.type === 'block_cooldown_scan_claim') {
      competingClaims.value.push(event.data)
    }
  }, { immediate: false })

  /**
   * 扫描所有 blocked_by_user / pending_system_reflection 联系人，
   * 处理冷静期到期触发申请生成、以及反思状态卡死兜底。
   *
   * 冷静期来源优先级：
   * 1. block.cooldownMinutes（每次拉黑时记录的本次冷静期）
   * 2. 全局默认 blockCooldownHours（旧数据 / 缺省值）
   *
   * 多 tab 并发：通过 broadcastChannel 做 leader election，避免重复触发 AI 申请。
   */
  async function scanBlockCooldowns() {
    if (!enabled.value)
      return

    // 多 tab leader election：先广播自己的 claim，等 50ms 看有没有更早的 claim
    const myClaim = { timestamp: Date.now(), tabId }
    competingClaims.value = []
    broadcastActivity({ type: 'block_cooldown_scan_claim', data: myClaim })
    await new Promise(resolve => setTimeout(resolve, 50))
    const earlier = competingClaims.value.find(c => c.timestamp < myClaim.timestamp || (c.timestamp === myClaim.timestamp && c.tabId < myClaim.tabId))
    if (earlier) {
      console.debug(`🔇 拉黑冷静期扫描跳过：另一个 tab (${earlier.tabId.slice(0, 8)}) 抢到了 leader`)
      return
    }

    const allContacts = await contactsStore.getAllContacts()
    const now = Date.now()
    const fallbackCooldownMs = blockCooldownHours.value * 3600 * 1000

    // E8 限流：单次扫描最多触发 N 个 AI 申请生成，避免同时到期 50+ 联系人时打满 LLM rate limit
    const MAX_TRIGGER_PER_SCAN = 3
    let triggerCount = 0

    for (const contact of allContacts) {
      if (contact.isGroup)
        continue
      const block = contact.blockStatus
      if (!block)
        continue

      // E2 时钟漂移防御：timestamp 在未来 / 远超 30 天前 → 视为系统时间被改动，重置 timestamp
      if (block.timestamp > now + 60_000) {
        console.warn(`⚠️ 联系人「${contact.name}」blockStatus.timestamp 在未来（${new Date(block.timestamp).toISOString()}），重置为 now`)
        contactsStore.resetBlockCooldown(contact.id)
        continue
      }
      if (now - block.timestamp > 30 * 24 * 3600 * 1000) {
        console.warn(`⚠️ 联系人「${contact.name}」blockStatus.timestamp 超过 30 天，可能是异常数据，重置`)
        contactsStore.resetBlockCooldown(contact.id)
        continue
      }

      // 1. blocked_by_user：冷静期到期 → 触发申请
      if (block.status === 'blocked_by_user') {
        if (!block.timestamp)
          continue
        const cooldownMs = typeof block.cooldownMinutes === 'number'
          ? block.cooldownMinutes * 60 * 1000
          : fallbackCooldownMs
        if (now - block.timestamp > cooldownMs) {
          if (triggerCount >= MAX_TRIGGER_PER_SCAN) {
            console.debug(`⏸️ scanBlockCooldowns 单轮限流 (${MAX_TRIGGER_PER_SCAN})，剩余冷静期到期联系人下一轮处理`)
            break
          }
          triggerCount++
          console.info(`💭 联系人「${contact.name}」冷静期已过（${typeof block.cooldownMinutes === 'number' ? `${block.cooldownMinutes}分钟` : '全局默认'}），触发 AI 反思生成申请...`)
          contactsStore.setPendingSystemReflection(contact.id)
          triggerAiFriendApplication(contact.id).catch((e) => {
            console.warn(`⚠️ 联系人「${contact.name}」生成申请失败:`, e)
            contactsStore.resetBlockCooldown(contact.id)
          })
        }
      }

      // 2. pending_system_reflection：超时兜底
      if (block.status === 'pending_system_reflection') {
        if (now - block.timestamp > PENDING_REFLECTION_TIMEOUT_MS) {
          console.warn(`⏰ 联系人「${contact.name}」反思状态超时，退回 blocked_by_user`)
          contactsStore.resetBlockCooldown(contact.id)
        }
      }

      // 注：pending_ai_approval 不再在后台兜底重试。
      // chat.ts streamText finish 阶段的"AI 沉默自动 reject"机制（F1 修复，line ~2301）
      // 保证一次 send 必然推进状态，无需后台心跳重发，避免对弱模型循环烧 token。
    }
  }

  /**
   * 触发 AI 为「被用户拉黑」的联系人生成好友申请理由。
   * - 独立一次性调用，不写入 chat/conversation-turns
   * - 30s 超时 + try/catch，任何异常都 fallback 到 resetBlockCooldown
   */
  async function triggerAiFriendApplication(contactId: string) {
    const contact = contactsStore.getContact(contactId)
    if (!contact || contact.isGroup) {
      contactsStore.resetBlockCooldown(contactId)
      return
    }

    const ok = await ensureChatProvider()
    if (!ok || !sendOptions.value?.chatProvider) {
      console.info(`ℹ️ provider 未就绪，联系人「${contact.name}」的申请生成推迟到下个心跳`)
      contactsStore.resetBlockCooldown(contactId)
      return
    }

    // 获取角色卡 + 最近 10 条消息作为反思上下文
    let aiPersona = ''
    try {
      const airiCardStore = useAiriCardStore()
      const summary = getContactPersonaSummary(contactId, contactsStore, airiCardStore)
      aiPersona = summary || ''
    }
    catch {}

    const recentMessages = chatStore.messages.slice(-10).filter((m: any) => m.role === 'user' || m.role === 'assistant')
    const contextSummary = recentMessages.map((msg: any) => {
      const sender = msg.role === 'user' ? '用户' : contact.name
      const content = extractTextContent(msg.content).substring(0, 60)
      return `${sender}: ${content}`
    }).join('\n')

    const systemPrompt = `# 你的任务
你现在是角色「${contact.name}」。你之前被用户（你的聊天对象）拉黑了，你们已经有一段时间没有联系了。
现在，你非常希望能够和好。请仔细分析下面的「被拉黑前的对话摘要」，理解当时发生了什么，
思考一个真诚的、符合你人设、并且【针对具体事件】的申请理由。

# 你的角色设定
${aiPersona || '（无角色卡信息）'}

# 被拉黑前的对话摘要（这是你被拉黑的关键原因）
${contextSummary || '（没有找到相关的对话记录）'}

# 指令
你需要调用 respond_to_unblock_opportunity 工具来决定是否申请解除拉黑。

- 如果决定申请：respond_to_unblock_opportunity(decision="apply", reason="写下你想对用户说的、真诚的、有针对性的申请理由")
- 如果决定暂时不申请：respond_to_unblock_opportunity(decision="skip")

请根据上述角色设定和对话摘要，调用工具作出决定。`

    const TIMEOUT_MS = 30_000

    try {
      const { stream: streamFn } = useLLM()
      const provider = sendOptions.value!.chatProvider!
      const model = sendOptions.value!.model
      const providerConfig = sendOptions.value!.providerConfig

      // 关系工具：业务逻辑在 execute 里（四条路径都会调它，且参数已解析为对象）
      const { buildRelationshipTools } = await import('../tools/relationship-actions')
      let toolWasCalled = false
      const relationshipTools = buildRelationshipTools({
        onUnblockRequest: (decision, reason) => {
          toolWasCalled = true
          if (decision === 'apply' && reason && reason.trim().length > 0) {
            contactsStore.setPendingUserApproval(contactId, reason.trim())
            console.info(`✅ 联系人「${contact.name}」生成申请理由："${reason.trim()}"`)
          }
          else {
            console.info(`ℹ️ 联系人「${contact.name}」决定暂不申请，重置冷静期`)
            contactsStore.resetBlockCooldown(contactId)
          }
        },
      })

      let settled = false
      await new Promise<void>((resolve, reject) => {
        const timeout = workerTimer.setTimeout(() => {
          if (!settled) {
            settled = true
            reject(new Error('triggerAiFriendApplication timeout'))
          }
        }, TIMEOUT_MS)

        const safeResolve = () => {
          if (!settled) {
            settled = true
            workerTimer.clearTimeout(timeout)
            resolve()
          }
        }
        const safeReject = (err: unknown) => {
          if (!settled) {
            settled = true
            workerTimer.clearTimeout(timeout)
            reject(err)
          }
        }

        streamFn(
          model,
          provider,
          [{ role: 'system', content: systemPrompt }],
          {
            providerConfig: providerConfig as any,
            priority: ApiPriority.LOW,
            tools: relationshipTools,
            onStreamEvent: (event: any) => {
              if (event.type === 'finish')
                safeResolve()
              else if (event.type === 'error')
                safeReject(new Error(event.error))
            },
          } as any,
        ).catch(safeReject)
      })

      // 未调用工具（模型不支持/跑偏）→ 重置冷静期，下轮再试
      if (!toolWasCalled) {
        console.warn(`⚠️ 联系人「${contact.name}」未调用 respond_to_unblock_opportunity 工具，按暂不申请处理`)
        contactsStore.resetBlockCooldown(contactId)
      }
    }
    catch (e) {
      console.warn(`⚠️ 联系人「${contact.name}」申请生成异常，重置冷静期:`, e)
      contactsStore.resetBlockCooldown(contactId)
    }
  }

  /**
   * 📱 页面可见性变化处理
   * 当用户从后台切回前台时，检查倒计时是否在冻结期间过期
   * 这是对抗移动端杀后台的关键机制
   */
  let visibilityListenerRegistered = false

  function handleVisibilityChange() {
    if (!enabled.value) {
      return
    }

    if (document.visibilityState === 'hidden') {
      // 仅在后台运行轻量心跳；前台切换回来后立即停止。
      startHeartbeat()
      return
    }

    if (document.visibilityState !== 'visible') {
      return
    }

    stopHeartbeat()

    console.debug('👁️ 页面恢复可见，检查后台活动状态...')

    // 检查倒计时是否在后台冻结期间过期了
    if (savedCountdownEndTime.value && savedCountdownEndTime.value <= Date.now()) {
      // 倒计时已过期但 setTimeout 被冻结没执行
      // ⚠️ 不补触发！用户回来说明要聊天了，补发主动消息没意义
      console.info('⏰ 倒计时在后台冻结期间过期，丢弃并重新开始新倒计时')

      if (countdownTimer) {
        workerTimer.clearTimeout(countdownTimer)
        countdownTimer = null
      }

      savedCountdownEndTime.value = null
      startCountdown()
    }
    else if (!isCountdownRunning.value && !countdownTimer) {
      // 倒计时完全丢失了（比如被系统彻底回收），重新启动
      console.info('🔄 倒计时丢失，重新启动')
      startCountdown()
    }

    // 🔄 恢复朋友圈定时调度（后台冻结会导致 workerTimer 丢失）
    scheduleMomentsCheck()

    // 🔄 恢复持久化的待触发朋友圈响应
    recoverPendingResponses()
  }

  /**
   * 启动后台活动引擎（同时启动心跳和倒计时）
   */
  const start = () => {
    if (!enabled.value) {
      return
    }

    if (engineStarted) {
      return
    }

    engineStarted = true
    console.info(`🚀 后台活动引擎启动（心跳 ${HEARTBEAT_INTERVAL}秒 / 倒计时 ${minHours.value}~${maxHours.value}h / 安静 ${quietTimeStart.value}-${quietTimeEnd.value}）`)

    // 🆕 启动时立即扫描一次拉黑冷静期，确保用户重新打开 PWA 时不需要等 60 秒心跳
    scanBlockCooldowns().catch(e => console.warn('⚠️ 启动时拉黑冷静期扫描失败:', e))

    // 🛡️ 时钟回拨自愈：如果用户改过设备时间，导致存储的时间戳"在未来"，
    // 直接清零（业务时间戳）或清空（定时器结束时间）以恢复到合理状态。
    const now = Date.now()
    const MAX_FUTURE_MOMENTS = 4 * 60 * 60 * 1000 // 朋友圈周期最多 1.5h+30min，给 4h 余量
    const MAX_FUTURE_COUNTDOWN = 24 * 60 * 60 * 1000 // 主动消息最多 6h，给 24h 余量
    if (lastMomentsCheck.value > now) {
      console.warn('⚠️ 检测到 lastMomentsCheck 在未来（设备时间被回拨？），重置为 0')
      lastMomentsCheck.value = 0
    }
    if (lastMomentsProduced.value > now) {
      console.warn('⚠️ 检测到 lastMomentsProduced 在未来，重置为 0')
      lastMomentsProduced.value = 0
    }
    if (lastMomentsFailureTime.value > now) {
      console.warn('⚠️ 检测到 lastMomentsFailureTime 在未来，重置为 0')
      lastMomentsFailureTime.value = 0
      consecutiveFailures.value = 0
    }
    if (savedMomentsCheckEndTime.value && savedMomentsCheckEndTime.value > now + MAX_FUTURE_MOMENTS) {
      console.warn('⚠️ 检测到 savedMomentsCheckEndTime 超出合理未来窗口，清空')
      savedMomentsCheckEndTime.value = null
    }
    if (savedCountdownEndTime.value && savedCountdownEndTime.value > now + MAX_FUTURE_COUNTDOWN) {
      console.warn('⚠️ 检测到 savedCountdownEndTime 超出合理未来窗口，清空')
      savedCountdownEndTime.value = null
    }
    // 跨页签锁可能因为时钟回拨变成"未来 1 小时"，超过 TTL 直接清零
    if (momentsSimulationLockUntil.value > now + MOMENTS_SIMULATION_LOCK_TTL_MS) {
      momentsSimulationLockUntil.value = 0
    }
    // contactPostWeights 同样可能被回拨污染
    // 使用 Object.keys 而非 for...in，避免原型链污染（虽然 useLocalStorage 反序列化的 plain object 没有原型链问题，但保持防御）
    const weights = contactPostWeights.value
    let weightsDirty = false
    const cleanedWeights: Record<string, number> = {}
    for (const id of Object.keys(weights)) {
      if (weights[id] > now) {
        cleanedWeights[id] = 0
        weightsDirty = true
      }
      else {
        cleanedWeights[id] = weights[id]
      }
    }
    if (weightsDirty) {
      console.warn('⚠️ 检测到 contactPostWeights 中有未来时间戳，已清零')
      contactPostWeights.value = cleanedWeights
    }

    // === 组合保活策略 ===
    // 🔒 1. Web Locks API 保活（让浏览器认为页面有活跃锁操作）
    startWebLockKeepAlive()
    // 💓 2. Worker 定时器心跳（不受后台节流，定期写 localStorage 保持活跃）
    startHeartbeat()
    // 👁️ 4. visibilitychange 事件（下方注册，对抗后台冻结）
    // 📢 5. Android 持久通知（切后台时自动显示，回前台自动关闭，见 handleVisibilityChange）
    // 📦 6. Service Worker（如项目已配置 PWA 则自动生效）

    // 📱 注册 visibilitychange 监听（只注册一次）
    // 当页面从后台恢复时，检查倒计时是否在冻结期间过期
    if (!visibilityListenerRegistered) {
      document.addEventListener('visibilitychange', handleVisibilityChange)
      visibilityListenerRegistered = true
    }

    // 启动倒计时（主动消息）- 优先尝试恢复之前的倒计时
    if (!isCountdownRunning.value) {
      const recovered = recoverCountdown()
      if (!recovered) {
        startCountdown()
      }
    }

    // 🎯 启动定期朋友圈模拟调度（高频低量：每 1~2h 检查，每次最多 1 人发帖）
    // 先尝试恢复持久化的预计触发时间，再启动调度
    recoverMomentsCheck()
    scheduleMomentsCheck()

    // 🔄 恢复持久化的待触发朋友圈响应（app 重启后补发）
    recoverPendingResponses()
  }

  /**
   * 停止后台活动引擎（同时停止心跳和倒计时）
   */
  const stop = () => {
    // === 停止所有保活策略 ===
    stopWebLockKeepAlive()
    stopHeartbeat()

    // 停止定期朋友圈模拟
    if (momentsPeriodicTimer) {
      workerTimer.clearTimeout(momentsPeriodicTimer)
      momentsPeriodicTimer = null
    }
    savedMomentsCheckEndTime.value = null
    // 重置调度/触发的 in-flight 状态，防止 stop 后再 start 时锁残留
    isSchedulingMoments = false
    isMomentsSimulating = false
    momentsSimulationLockUntil.value = 0

    // 清理所有延迟响应 timer
    for (const id of responseTimerIds.values()) {
      workerTimer.clearTimeout(id)
    }
    responseTimerIds.clear()

    if (countdownTimer) {
      workerTimer.clearTimeout(countdownTimer)
      countdownTimer = null
      isCountdownRunning.value = false
      countdownEndTime = null
      savedCountdownEndTime.value = null // 清除持久化的倒计时
      console.info('⏸️  后台活动倒计时已停止')
    }

    if (visibilityListenerRegistered) {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      visibilityListenerRegistered = false
    }

    // ⚠️ 不要调用 workerTimer.terminate()！
    // terminate 会永久销毁 Worker，之后再 start() 时所有定时器会降级到主线程（被后台节流）
    // stop() 中各个 stopXxx() 已经清理了所有活跃定时器，Worker 本身保持存活以备下次 start()

    engineStarted = false
    console.info('⏸️  后台活动引擎已完全停止')
  }

  /**
   * 手动触发一次（用于测试）
   */
  const manualTrigger = async () => {
    console.info('🔧 手动触发后台活动')
    await triggerAIAction()
  }

  /**
   * 处理AI的后台活动响应
   * 从AI的自然语言回复中提取行动意图
   */
  const handleAIActionResponse = (aiResponse: string): boolean => {
    if (import.meta.env.DEV) {
      console.debug('📥 AI后台决策原始响应:', aiResponse.length > 500 ? `${aiResponse.substring(0, 500)}...` : aiResponse)
    }

    // 提取完整的文本内容（包括JSON之前的部分 + JSON中的text）
    let textContent = aiResponse

    // 1. 移除表情标记和延迟标记
    textContent = textContent.replace(/<\|EMOTE_\w+\|>/g, '')
    textContent = textContent.replace(/<\|DELAY:\d+\|>/g, '')

    // 2. 尝试提取并合并JSON中的所有文本字段（text, reason, dialogue等）
    try {
      const jsonMatch = aiResponse.match(/\{[\s\S]*?\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])

        if (parsed.decision === 'accept') {
          const activeContact = contactsStore.activeContact
          const characterId = activeContact?.id || String(airiCardStore.activeCard?.metadata?.id || airiCardStore.activeCard?.name || 'AI')
          const characterName = activeContact?.name || airiCardStore.activeCard?.name || 'AI'
          const characterAvatar = activeContact?.avatar || '/BOT.jpg'
          const greeting = typeof parsed.greeting === 'string' ? parsed.greeting : undefined

          callStore.setIncomingCall(
            'voice',
            characterId,
            characterName,
            characterAvatar,
            greeting,
          )
          console.info('📞 AI决策: 接受语音通话，已触发来电')
          return true
        }

        // 收集所有可能包含文本的字段
        const textParts = []

        // 提取JSON之前的文本
        const beforeJson = aiResponse.substring(0, aiResponse.indexOf(jsonMatch[0])).trim()
        const cleanBeforeJson = beforeJson.replace(/<\|EMOTE_\w+\|>/g, '').replace(/<\|DELAY:\d+\|>/g, '').trim()
        if (cleanBeforeJson) {
          textParts.push(cleanBeforeJson)
        }

        // 提取JSON中的文本字段（包括reason字段）
        if (parsed.text)
          textParts.push(parsed.text)
        if (parsed.reason)
          textParts.push(parsed.reason)
        if (parsed.dialogue)
          textParts.push(parsed.dialogue)
        if (parsed.content)
          textParts.push(parsed.content)

        if (textParts.length > 0) {
          textContent = textParts.join(' ')
          console.info('📝 提取的完整文本:', textContent)
        }
        else {
          textContent = aiResponse.trim()
        }
      }
      else {
        // 没有JSON格式，直接清理原始文本
        textContent = textContent.trim()
        console.info('📝 纯文本内容:', textContent)
      }
    }
    catch {
      // JSON解析失败，使用清理后的原始文本
      textContent = textContent.trim()
    }

    // 从自然语言中检测意图
    const lowerText = textContent.toLowerCase()

    // 🔧 排除否定/拒绝/疑问的情况
    const negativePatterns = [
      '不.*电话',
      '不.*通话',
      '不.*视频',
      '不想.*电话',
      '不想.*通话',
      '要不要.*电话', // "要不要打电话？"是疑问
      '要.*电话.*吗', // "要打电话吗？"是疑问
      '能.*电话.*吗', // "能打电话吗？"是疑问
      '可以.*电话.*吗', // "可以打电话吗？"是疑问
      '等.*再.*电话',
      '等.*再.*通话',
      '先.*文字',
      '现在.*不',
      '暂时.*不',
      '不打扰',
      '算了',
      '不合适',
    ]

    // 检查是否包含"稍等"、"马上"等积极词汇，如果有则跳过否定检测
    const hasPositiveIntent = lowerText.includes('稍等') || lowerText.includes('马上') || lowerText.includes('立刻') || lowerText.includes('现在就')

    if (!hasPositiveIntent) {
      for (const pattern of negativePatterns) {
        const regex = new RegExp(pattern)
        if (regex.test(lowerText)) {
          console.info('🤐 AI决策: 保持沉默/拒绝')
          return false
        }
      }
    }

    // 检测视频通话意图（需要明确的意图）
    if (lowerText.includes('发起视频通话') || lowerText.includes('我想和你视频') || lowerText.includes('我们视频通话') || lowerText.includes('视频聊聊')) {
      console.info('📹 AI决策: 发起视频通话')
      // 注意：实际的setIncomingCall会在chat.ts的detectCallIntent中处理
      return false
    }

    // 检测语音通话意图（需要明确的意图）
    const voiceCallKeywords = [
      '发起语音通话',
      '我想给你打个电话',
      '我给你打电话',
      '我现在给你打电话',
      '我们语音通话',
      '语音聊聊',
      '马上拨过去',
      '现在拨过去',
      '马上打过去',
      '立刻拨过去',
      '这不就在打',
      '就在打',
      '正在打',
      '给你打过去',
      '打过去',
      '这就打',
      '现在就打',
    ]
    const voiceCallPatterns = [
      /稍等.{0,10}拨/,
      /稍等.{0,10}打/,
      /这.{0,5}就.{0,5}打/,
      /正在.{0,10}打/,
    ]

    if (voiceCallKeywords.some(keyword => lowerText.includes(keyword))
      || voiceCallPatterns.some(pattern => pattern.test(lowerText))) {
      console.info('📞 AI决策: 发起语音通话')
      // 注意：实际的setIncomingCall会在chat.ts的detectCallIntent中处理
      return false
    }

    // 其他情况：AI发送了普通消息
    console.info('💬 AI决策: 主动发送消息')
    return false
  }

  async function syncAllContactConfigs(): Promise<number | undefined> {
    if (!offlineSyncStore.isConfigured || !offlineSyncStore.enabled) {
      if (import.meta.env.DEV)
        console.info('[BackgroundActivity] 离线同步未配置，跳过自动同步后台活动状态')
      return undefined
    }

    const providerCfg = sendOptions.value?.providerConfig
    const apiKey = typeof providerCfg === 'object' && providerCfg !== null && 'apiKey' in providerCfg
      ? String(providerCfg.apiKey)
      : ''
    const baseUrl = typeof providerCfg === 'object' && providerCfg !== null && 'baseUrl' in providerCfg
      ? String(providerCfg.baseUrl)
      : ''
    const modelId = sendOptions.value?.model || ''

    const groupContacts = contactsStore.contactsList.filter(contact => contact.id && contact.isGroup)
    await Promise.all(groupContacts.map(contact => offlineSyncStore.deleteContactConfig(contact.id)))

    // 批量压缩头像，限制并发数避免主线程阻塞
    const contactsList = contactsStore.contactsList.filter(c => c.id && c.name && !c.isGroup)
    const avatarCache = new Map<string, string | undefined>()

    // 分批压缩头像，每批最多3个并发
    const BATCH_SIZE = 3
    for (let i = 0; i < contactsList.length; i += BATCH_SIZE) {
      const batch = contactsList.slice(i, i + BATCH_SIZE)
      await Promise.all(
        batch.map(async (contact) => {
          if (contact.avatar) {
            try {
              const compressed = await compressAvatarForPush(contact.avatar)
              avatarCache.set(contact.id, compressed)
            }
            catch (err) {
              console.warn(`[BackgroundActivity] 压缩头像失败 (${contact.name}):`, err)
              avatarCache.set(contact.id, undefined)
            }
          }
          else {
            avatarCache.set(contact.id, undefined)
          }
        }),
      )
    }

    const configs = await Promise.all(
      contactsList.map(async (contact) => {
        const card = contact.cardId ? airiCardStore.getCard(contact.cardId) : undefined
        const systemPrompt = card
          ? [card.systemPrompt, card.description, card.personality].filter(Boolean).join('\n')
          : ''
        const allMessages = chatStore.getMessagesForContact(contact.id)
        const recentMessages = allMessages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .slice(-50)
          .map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' }))

        return {
          contactId: contact.id,
          apiKey,
          baseUrl: baseUrl || undefined,
          modelId: modelId || undefined,
          systemPrompt,
          characterName: contact.name || '角色',
          characterAvatar: avatarCache.get(contact.id),
          recentMessages: JSON.stringify(recentMessages),
          minHours: minHours.value,
          maxHours: maxHours.value,
          quietTimeStart: quietTimeStart.value,
          quietTimeEnd: quietTimeEnd.value,
          timezoneOffset: new Date().getTimezoneOffset(),
          enabled: enabled.value && !contactsStore.isFullyBlocked(contact.id),
          backgroundMessageEnabled: contact.backgroundMessageEnabled !== false,
          isGroup: contact.isGroup === true,
        }
      }),
    )

    if (configs.length === 0)
      return 0

    try {
      const synced = await offlineSyncStore.syncConfigs(configs)
      if (import.meta.env.DEV) {
        console.info(`[BackgroundActivity] 已自动同步后台活动开关状态到服务器，同步 ${synced} 个联系人`)
      }
      return synced
    }
    catch (error) {
      console.warn('[BackgroundActivity] 自动同步后台活动开关状态失败:', error)
      throw error
    }
  }

  watch(enabled, (isEnabled) => {
    syncAllContactConfigs().catch(error => console.warn('[BackgroundActivity] 自动同步任务异常:', error))

    if (isEnabled) {
      start()
    }
    else {
      stop()
    }
  })

  // 📡 监听跨页面后台活动广播
  watch(receivedActivity, (event) => {
    if (!event)
      return

    console.info('📨 收到后台活动广播:', event.type)

    switch (event.type) {
      case 'ai_activity_triggered':
        console.info('🤖 其他页面触发了AI后台活动')
        // 其他页面已经触发，本页面无需重复操作
        break

      case 'countdown_reset':
        console.info('🔄 其他页面重置了倒计时')
        // 倒计时状态会通过 LocalStorage 自动同步
        break
    }
  })

  return {
    // 状态配置
    enabled,
    minHours, // 最小倒计时时间（小时）- 用于主动消息
    maxHours, // 最大倒计时时间（小时）- 用于主动消息
    quietTimeStart, // 安静时间开始
    quietTimeEnd, // 安静时间结束
    blockCooldownHours, // 拉黑冷静期（小时）
    isCountdownRunning, // 倒计时运行状态
    lastMomentsCheck, // 最后朋友圈尝试时间
    lastMomentsProduced, // 最后真正产出动态的时间（兜底/补发判定基于此）

    // 方法
    start, // 启动后台活动引擎（心跳 + 倒计时）
    stop, // 停止后台活动引擎
    setSendOptions,
    startHeartbeat, // 单独启动心跳
    stopHeartbeat, // 单独停止心跳
    heartbeatTick, // 手动触发一次心跳
    startCountdown, // 单独启动倒计时
    resetCountdown, // 用户回复时重置倒计时
    manualTrigger, // 手动触发一次主动消息
    triggerAIAction,
    handleAIActionResponse,
    isQuietTime, // 检查是否在安静时间
    triggerMomentsSimulation, // 手动触发朋友圈模拟
    checkAndRunMomentsSimulation, // 检查并运行朋友圈模拟（离线后回来时调用）
    triggerAIResponseToUserPost, // 用户发动态后触发AI响应
    triggerAIResponseToUserComment, // 用户评论动态后触发AI回复
    sendOptions, // LLM 配置（供 push-server 同步使用）
    scanBlockCooldowns, // 主动触发一次拉黑冷静期扫描（用于切换联系人/打开 PWA 时即时触发）
    syncAllContactConfigs,
  }
})
